import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

interface Tournament {
  id: string;
  name: string;
  format?: string; // 'SINGLES' | 'DOUBLES'
  status?: string;
  startDate?: string;
  endDate?: string;
}

interface TournamentLevel {
  level: string;
  playerCount: number;
}

interface Player {
  id: string;
  name: string;
  telegramChatId?: string;
}

interface Club {
  id: string;
  name: string;
}

interface Court {
  id: string;
  name: string;
  clubId: string;
  club?: {
    id: string;
    name: string;
  };
}

interface Match {
  id: string;
  tournamentId: string;
  level?: string | null;
  matchNumber?: number | null;
  stageMatchNumber?: number | null;
  groupId: string | null;
  player1Id?: string;
  player2Id?: string;
  player3Id?: string;
  player4Id?: string;
  player1?: Player;
  player2?: Player;
  player3?: Player;
  player4?: Player;
  winnerId?: string;
  winner2Id?: string;
  winner?: Player;
  winner2?: Player;
  status: string;
  score?: string;
  result?: string;
  matchFormatId?: string;
  matchFormat?: {
    id: string;
    name: string;
    maxSets: number;
    setsToWin: number;
    hasTiebreak: boolean;
    tiebreakTo: number;
  };
  group?: {
    id: string;
    name: string;
    level?: string;
  } | null;
  round?: {
    id: string;
    name: string;
    order: number;
  } | null;
  matchCode?: string | null;
  scheduledDate?: string;
  scheduledTime?: string;
  court?: {
    id: string;
    name: string;
    club?: {
      id: string;
      name: string;
    };
  };
  reservation?: {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
    court?: {
      id: string;
      name: string;
      club?: {
        id: string;
        name: string;
      };
    } | null;
  } | null;
}

interface SetScore {
  set: number;
  player1: number;
  player2: number;
  tiebreak?: { player1: number; player2: number } | null; // Полный счет тайбрейка
  superTiebreak?: boolean; // Флаг решающего тайбрейка
}

interface MatchResult {
  sets: SetScore[];
  walkover?: boolean;
  retired?: {
    player: 'player1' | 'player2';
    reason?: string;
  };
  specialStatus?: {
    type: 'REFUSAL' | 'DISQUALIFICATION' | 'INJURY' | 'NO_SHOW' | 'SUSPENSION';
    player?: 'player1' | 'player2';
    reason?: string;
    reasonCode?: string;
  };
  administrative?: {
    players?: Partial<Record<'player1' | 'player2' | 'player3' | 'player4', {
      noShow?: boolean;
      excludeFromStats?: boolean;
      excludeFromRatingPoints?: boolean;
      cancelEntryFeeAccrual?: boolean;
    }>>;
    excludeFromElo?: boolean;
    suppressTelegramResults?: boolean;
    reason?: string;
  };
  completedAt: string;
}

type AdministrativeSlot = 'player1' | 'player2' | 'player3' | 'player4';
type AdministrativePlayerFlags = {
  noShow: boolean;
  excludeFromStats: boolean;
  excludeFromRatingPoints: boolean;
  cancelEntryFeeAccrual: boolean;
};

interface RoundOption {
  id: string;
  name: string;
  order: number;
}

interface GenerateMatchPreviewItem {
  groupId: string;
  groupName: string;
  level: string;
  player1Id?: string | null;
  player2Id?: string | null;
  player3Id?: string | null;
  player4Id?: string | null;
  player1Name?: string | null;
  player2Name?: string | null;
  player3Name?: string | null;
  player4Name?: string | null;
  participantsLabel: string;
  reasonCode: string;
  reason: string;
}

interface GenerateMatchesPreviewResponse {
  preview?: boolean;
  created?: number;
  toCreate?: number;
  total?: number;
  existing?: number;
  matches?: GenerateMatchPreviewItem[];
  message?: string;
}

const AUTO_SPECIAL_STATUS_TYPES = new Set(['REFUSAL', 'DISQUALIFICATION', 'INJURY', 'NO_SHOW']);

type SuspensionStatusOption = 'SUSPENSION_TIME' | 'SUSPENSION_WEATHER' | 'SUSPENSION_OTHER';

const inferSuspensionStatusOption = (reason?: string | null, reasonCode?: string | null): SuspensionStatusOption => {
  const normalizedCode = String(reasonCode || '').trim().toUpperCase();
  if (normalizedCode === 'WEATHER') return 'SUSPENSION_WEATHER';
  if (normalizedCode === 'TIME') return 'SUSPENSION_TIME';
  if (normalizedCode === 'OTHER' || normalizedCode === 'SUSPENDED') return 'SUSPENSION_OTHER';
  const normalized = String(reason || '').trim().toLowerCase();
  if (!normalized) return 'SUSPENSION_OTHER';
  if (normalized.includes('погод') || normalized.includes('weather')) return 'SUSPENSION_WEATHER';
  if (normalized.includes('врем') || normalized.includes('time')) return 'SUSPENSION_TIME';
  return 'SUSPENSION_OTHER';
};

const SPECIAL_STATUS_LABELS: Record<string, string> = {
  REFUSAL: 'Отказ',
  DISQUALIFICATION: 'Дисквалификация',
  INJURY: 'Травма',
  NO_SHOW: 'Неявка',
  SUSPENSION: 'int'
};

const ADMIN_SLOTS: AdministrativeSlot[] = ['player1', 'player2', 'player3', 'player4'];

const createDefaultAdministrativePlayerFlags = (): AdministrativePlayerFlags => ({
  noShow: false,
  excludeFromStats: false,
  excludeFromRatingPoints: false,
  cancelEntryFeeAccrual: false
});

const isTeam2WinnerInMatch = (match: Match): boolean => {
  // Для парных матчей winnerId может быть ID любого игрока команды 2.
  const team2PlayerIds = [match.player2Id, match.player4Id].filter(Boolean) as string[];
  if (team2PlayerIds.length > 0 && match.winnerId) {
    return team2PlayerIds.includes(match.winnerId);
  }
  // Для одиночных матчей.
  return !!match.winnerId && match.winnerId === match.player2Id;
};

const shouldRenderAsSuperTiebreak = (
  set: { player1: number; player2: number; superTiebreak?: boolean },
  superTiebreakTo: number = 10
): boolean => {
  // Иногда в старых/ручных данных флаг superTiebreak мог попасть в обычный сет (7:6).
  // Показываем сет как "решающий тайбрейк" только если счет действительно тайбрейковый (>= superTiebreakTo).
  if (set.superTiebreak !== true) return false;
  return Math.max(Number(set.player1 || 0), Number(set.player2 || 0)) >= Math.max(1, superTiebreakTo);
};

// Функция форматирования счета матча
const formatMatchScore = (match: Match): string => {
  // Если есть result (JSON), используем его для форматирования
  if (match.result) {
    try {
      const result: MatchResult = JSON.parse(match.result);
      
      // Формируем счет, если есть сеты
      let scoreString = '';
      if (result.sets && result.sets.length > 0) {
        // Проверяем, есть ли специальный статус (матч не завершен)
        const hasSpecialStatus = !!result.specialStatus;
        
        // Подсчитываем общий счет по сетам
        let player1Sets = 0;
        let player2Sets = 0;
        
        // Подсчитываем завершенные сеты
        result.sets.forEach((set, index) => {
          const isLastSet = index === result.sets.length - 1;
          const hasTiebreak = set.tiebreak !== null && set.tiebreak !== undefined;
          
          // Определяем, является ли это решающим тайбрейком
          // ТОЛЬКО по явному флагу - не определяем по счету геймов!
          const isSuperTiebreak = set.superTiebreak === true;
          
          // Определяем, завершен ли сет
          let isSetCompleted = false;
          
          if (isSuperTiebreak) {
            // Для решающего тайбрейка проверяем, завершен ли он
            if (hasTiebreak && set.tiebreak !== null) {
              // Если тайбрейк указан, проверяем по тайбрейку
              if (typeof set.tiebreak === 'object' && 'player1' in set.tiebreak && 'player2' in set.tiebreak) {
                const { player1: tb1, player2: tb2 } = set.tiebreak;
                // Решающий тайбрейк завершен, если один из игроков достиг 10 очков с разницей минимум 2
                const maxScore = Math.max(tb1, tb2);
                const minScore = Math.min(tb1, tb2);
                isSetCompleted = maxScore >= 10 && (maxScore - minScore) >= 2;
              } else if (typeof set.tiebreak === 'number') {
                // Старый формат - считаем завершенным, если есть тайбрейк и счет >= 10
                isSetCompleted = set.tiebreak >= 10;
              }
            } else {
              // Если тайбрейк не указан, но счет геймов > 7, проверяем по счету геймов
              // Решающий тайбрейк завершен, если один из игроков достиг 10 очков с разницей минимум 2
              const maxScore = Math.max(set.player1, set.player2);
              const minScore = Math.min(set.player1, set.player2);
              isSetCompleted = maxScore >= 10 && (maxScore - minScore) >= 2;
            }
          } else if (hasTiebreak) {
            // Обычный тайбрейк - считаем завершенным, если есть тайбрейк
            isSetCompleted = true;
          } else {
            // Обычный сет - завершен, если один из игроков выиграл (>= 6 с разницей >= 2, или 7)
            if (set.player1 > set.player2) {
              // player1 выиграл, если player1 >= 6 и разница >= 2, или player1 = 7
              isSetCompleted = (set.player1 >= 6 && (set.player1 - set.player2) >= 2) || set.player1 === 7;
            } else if (set.player2 > set.player1) {
              // player2 выиграл
              isSetCompleted = (set.player2 >= 6 && (set.player2 - set.player1) >= 2) || set.player2 === 7;
            }
          }
          
          // Если есть специальный статус, считаем только завершенные сеты
          // Если специального статуса нет, считаем все сеты (матч завершен)
          if (!hasSpecialStatus || isSetCompleted) {
            // Определяем победителя сета с учетом тайбрейка
            let setWinner: 'player1' | 'player2' | null = null;
            
            if (hasTiebreak && set.tiebreak !== null) {
              // Если есть тайбрейк, победитель определяется по тайбрейку
              if (typeof set.tiebreak === 'object' && 'player1' in set.tiebreak && 'player2' in set.tiebreak) {
                const { player1: tb1, player2: tb2 } = set.tiebreak;
                if (tb1 > tb2) {
                  setWinner = 'player1';
                } else if (tb2 > tb1) {
                  setWinner = 'player2';
                }
              } else if (typeof set.tiebreak === 'number') {
                // Старый формат: тайбрейк - это счет победителя
                // Для супер-тайбрейка (10:7) или обычного тайбрейка (7:6)
                if (isSuperTiebreak) {
                  // Для супер-тайбрейка победитель определяется по счету геймов (очков)
                  if (set.player1 > set.player2) {
                    setWinner = 'player1';
                  } else if (set.player2 > set.player1) {
                    setWinner = 'player2';
                  }
                } else {
                  // Для обычного тайбрейка при счете 7:6 или 6:7
                  if (set.player1 === 7 && set.player2 === 6) {
                    setWinner = 'player1';
                  } else if (set.player1 === 6 && set.player2 === 7) {
                    setWinner = 'player2';
                  }
                }
              }
            } else {
              // Если тайбрейка нет, победитель определяется по счету геймов
              if (set.player1 > set.player2) {
                setWinner = 'player1';
              } else if (set.player2 > set.player1) {
                setWinner = 'player2';
              }
            }
            
            if (setWinner === 'player1') {
              player1Sets++;
            } else if (setWinner === 'player2') {
              player2Sets++;
            }
          }
        });
        
        // Формируем строку счета каждого сета
        const setsScores = result.sets.map(set => {
          // Для супер-тайбрейка не добавляем скобки с тайбрейком
          if (shouldRenderAsSuperTiebreak(set, match.matchFormat?.superTiebreakTo || 10)) {
            return `${set.player1}:${set.player2}`;
          }
          
          if (set.tiebreak !== null && set.tiebreak !== undefined) {
            // Поддержка как нового формата (объект), так и старого (число)
            if (typeof set.tiebreak === 'object' && set.tiebreak.player1 !== undefined) {
              // Показываем только счет проигравшего (меньший счет)
              const loserScore = Math.min(set.tiebreak.player1, set.tiebreak.player2);
              return `${set.player1}:${set.player2}(${loserScore})`;
            } else if (typeof set.tiebreak === 'number') {
              // Старый формат (только счет победителя) - оставляем как есть
              return `${set.player1}:${set.player2}(${set.tiebreak})`;
            }
          }
          return `${set.player1}:${set.player2}`;
        }).join(' ');
        
        // Формат: общий_счет_по_сетам (счет_каждого_сета)
        scoreString = `${player1Sets}:${player2Sets} (${setsScores})`;
      }
      
      // Обрабатываем специальные статусы
      if (result.specialStatus) {
        const label = SPECIAL_STATUS_LABELS[result.specialStatus.type] || result.specialStatus.type;

        // Для служебных статусов в групповых матчах показываем фиксированный сетовый счет.
        if (AUTO_SPECIAL_STATUS_TYPES.has(result.specialStatus.type)) {
          const team2Winner = isTeam2WinnerInMatch(match);
          const setsToWin = match.matchFormat?.setsToWin || 2;
          const shortStatus =
            result.specialStatus.type === 'NO_SHOW' ||
            result.specialStatus.type === 'INJURY' ||
            result.specialStatus.type === 'REFUSAL'
              ? 'W.O.'
              : label.toLowerCase();

          let player1Sets = 0;
          let player2Sets = 0;
          let gamesPart = '';

          if (scoreString) {
            const setsMatch = scoreString.match(/^(\d+):(\d+)/);
            if (setsMatch) {
              player1Sets = parseInt(setsMatch[1], 10) || 0;
              player2Sets = parseInt(setsMatch[2], 10) || 0;
            }

            const gamesMatch = scoreString.match(/\((.+)\)$/);
            if (gamesMatch) {
              gamesPart = gamesMatch[1];
            }
          }

          if (team2Winner) {
            player2Sets = Math.max(player2Sets, setsToWin);
          } else {
            player1Sets = Math.max(player1Sets, setsToWin);
          }

          const gamesWithStatus = gamesPart ? `(${gamesPart} ${shortStatus})` : `(${shortStatus})`;
          return `${player1Sets}:${player2Sets} ${gamesWithStatus}`;
        }

        let statusString = label;
        if (result.specialStatus.reason) {
          statusString = `${label} (${result.specialStatus.reason})`;
        }
        
        // Если есть счет, выводим статус третьей строкой (через <br>)
        if (scoreString) {
          return `${scoreString}\n${statusString}`;
        }
        return statusString;
      }
      
      // Обрабатываем walkover (для обратной совместимости)
      if (result.walkover) {
        return scoreString ? `${scoreString} / W.O.` : '2:0 (W.O.)';
      }
      
      // Обрабатываем retired (для обратной совместимости)
      if (result.retired) {
        const retiredScore = result.retired.player === 'player1' ? '2:0' : '0:2';
        const retiredString = result.retired.reason 
          ? `${retiredScore} (Retired: ${result.retired.reason})`
          : `${retiredScore} (Retired)`;
        return scoreString ? `${scoreString} / ${retiredString}` : retiredString;
      }
      
      // Если есть только счет, возвращаем его
      if (scoreString) {
        return scoreString;
      }
    } catch (e) {
      console.error('Ошибка парсинга результата:', e);
    }
  }
  
  // Fallback на старый формат score, если result отсутствует
  return match.score || '-';
};

const MatchResults: React.FC = () => {
  const MATCH_RESULTS_SELECTION_STORAGE_KEY = 'match-results-selection-v1';
  const MATCH_RESULTS_GROUPS_SEND_SELECTION_STORAGE_KEY = 'match-results-groups-send-selection-v1';
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [levels, setLevels] = useState<TournamentLevel[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const [selectionRestored, setSelectionRestored] = useState(false);
  const [levelRestored, setLevelRestored] = useState(false);

  const tournamentStatusMeta: Record<string, { order: number; label: string }> = {
    ACTIVE: { order: 0, label: 'Активные' },
    PLANNED: { order: 1, label: 'Запланированные' },
    COMPLETED: { order: 2, label: 'Завершенные' },
    CANCELLED: { order: 3, label: 'Отмененные' }
  };

  const tournamentsGrouped = useMemo(() => {
    const toDateTs = (value?: string): number => {
      if (!value) return Number.NEGATIVE_INFINITY;
      const parsed = new Date(value).getTime();
      return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
    };

    const groups = new Map<string, { label: string; order: number; items: Tournament[] }>();
    for (const tournament of tournaments) {
      const statusKey = String(tournament.status || '').trim().toUpperCase();
      const statusInfo = tournamentStatusMeta[statusKey] || { order: 99, label: 'Прочие' };
      if (!groups.has(statusInfo.label)) {
        groups.set(statusInfo.label, { label: statusInfo.label, order: statusInfo.order, items: [] });
      }
      groups.get(statusInfo.label)!.items.push(tournament);
    }

    const sortedGroups = Array.from(groups.values()).sort((a, b) => a.order - b.order || a.label.localeCompare(b.label, 'ru'));
    for (const group of sortedGroups) {
      group.items.sort((a, b) => {
        const aTs = Math.max(toDateTs(a.endDate), toDateTs(a.startDate));
        const bTs = Math.max(toDateTs(b.endDate), toDateTs(b.startDate));
        if (aTs !== bTs) return bTs - aTs;
        return String(a.name || '').localeCompare(String(b.name || ''), 'ru');
      });
    }
    return sortedGroups;
  }, [tournaments]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showGeneratePreviewModal, setShowGeneratePreviewModal] = useState(false);
  const [generatePreviewData, setGeneratePreviewData] = useState<GenerateMatchesPreviewResponse | null>(null);
  const [generatingNextRound, setGeneratingNextRound] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [editingMatch, setEditingMatch] = useState<Match | null>(null);
  const [resultInput, setResultInput] = useState('');
  const [matchFormat, setMatchFormat] = useState<any>(null);
  const [availableFormats, setAvailableFormats] = useState<any[]>([]);
  const [sets, setSets] = useState<SetScore[]>([]);
  const [specialStatus, setSpecialStatus] = useState<{
    type: 'REFUSAL' | 'DISQUALIFICATION' | 'INJURY' | 'NO_SHOW' | 'SUSPENSION' | null;
    player: 'player1' | 'player2' | null;
    reason: string;
    reasonCode?: string;
  }>({ type: null, player: null, reason: '' });
  const [suspensionStatusOption, setSuspensionStatusOption] = useState<SuspensionStatusOption>('SUSPENSION_OTHER');
  const [administrativePlayers, setAdministrativePlayers] = useState<Partial<Record<AdministrativeSlot, AdministrativePlayerFlags>>>({});
  const [administrativeExcludeFromElo, setAdministrativeExcludeFromElo] = useState(false);
  const [administrativeSuppressTelegramResults, setAdministrativeSuppressTelegramResults] = useState(false);
  const [administrativeReason, setAdministrativeReason] = useState('');
  const [walkover, setWalkover] = useState(false);
  const [retired, setRetired] = useState<{
    player: 'player1' | 'player2' | null;
    reason: string;
  }>({ player: null, reason: '' });
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [savingResult, setSavingResult] = useState(false);
  const [resultDate, setResultDate] = useState('');
  const [resultTime, setResultTime] = useState('');
  const [resultDateTimeLocked, setResultDateTimeLocked] = useState(false);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [courts, setCourts] = useState<Court[]>([]);
  const [resultClubId, setResultClubId] = useState('');
  const [resultCourtId, setResultCourtId] = useState('');
  
  // Состояние для модального окна отправки результата
  const [showSendResultModal, setShowSendResultModal] = useState(false);
  const [matchForSend, setMatchForSend] = useState<Match | null>(null);
  const [sendResultTab, setSendResultTab] = useState<'copy' | 'groups'>('copy');
  const [allGroups, setAllGroups] = useState<any[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [selectedGroupsAndTopicsForResult, setSelectedGroupsAndTopicsForResult] = useState<Set<string>>(new Set());
  const [sendingResult, setSendingResult] = useState(false);
  
  // Состояние для информации о плей-офф (для правильного отображения названий раундов)
  const [playoffStage, setPlayoffStage] = useState<{
    rounds: Array<{ round: number; name: string; matches: number }>;
    directFinal?: boolean;
    thirdPlaceMatch?: boolean;
  } | null>(null);
  const [levelBracketFormat, setLevelBracketFormat] = useState<string | null>(null);
  const [levelPreScheduleSkeleton, setLevelPreScheduleSkeleton] = useState(false);
  const [repairingFirstRound, setRepairingFirstRound] = useState(false);
  const [editingStageMatch, setEditingStageMatch] = useState<Match | null>(null);
  const [editingStageLevel, setEditingStageLevel] = useState('');
  const [editingStageRoundId, setEditingStageRoundId] = useState('');
  const [editingStageRoundName, setEditingStageRoundName] = useState('');
  const [editingStageNumber, setEditingStageNumber] = useState('');
  const [editingStageRoundOptions, setEditingStageRoundOptions] = useState<RoundOption[]>([]);
  const [savingStageMeta, setSavingStageMeta] = useState(false);

  const specialStatusSelectValue: '' | 'REFUSAL' | 'DISQUALIFICATION' | 'INJURY' | 'NO_SHOW' | SuspensionStatusOption =
    !specialStatus.type
      ? ''
      : specialStatus.type === 'SUSPENSION'
        ? suspensionStatusOption
        : specialStatus.type;

  useEffect(() => {
    fetchTournaments();
    fetchClubsAndCourts();
  }, []);

  useEffect(() => {
    if (selectionRestored || tournaments.length === 0) return;
    try {
      const raw = localStorage.getItem(MATCH_RESULTS_SELECTION_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        tournamentId?: string;
        searchQuery?: string;
        selectedGroup?: string;
        selectedStatus?: string;
      };
      if (!parsed?.tournamentId) return;
      const savedTournament = tournaments.find(t => t.id === parsed.tournamentId);
      if (savedTournament) {
        setSelectedTournament(savedTournament);
      }
      setSearchQuery(String(parsed.searchQuery || ''));
      setSelectedGroup(String(parsed.selectedGroup || 'all'));
      setSelectedStatus(String(parsed.selectedStatus || 'all'));
    } catch (error) {
      console.error('Ошибка восстановления выбранного турнира (match-results):', error);
    } finally {
      setSelectionRestored(true);
    }
  }, [tournaments, selectionRestored]);

  useEffect(() => {
    if (selectedTournament) {
      fetchLevels();
    }
  }, [selectedTournament]);

  useEffect(() => {
    setLevelRestored(false);
  }, [selectedTournament?.id]);

  useEffect(() => {
    if (!selectedTournament || levels.length === 0 || levelRestored) return;
    if (selectedLevel && levels.some((lvl) => lvl.level === selectedLevel)) {
      setLevelRestored(true);
      return;
    }

    try {
      const raw = localStorage.getItem(MATCH_RESULTS_SELECTION_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) as { tournamentId?: string; level?: string } : null;
      const savedLevel =
        parsed?.tournamentId === selectedTournament.id
          ? (parsed?.level || '')
          : '';
      if (savedLevel && levels.some((lvl) => lvl.level === savedLevel)) {
        setSelectedLevel(savedLevel);
        setLevelRestored(true);
        return;
      }
    } catch (error) {
      console.error('Ошибка восстановления выбранного уровня (match-results):', error);
    }

    setSelectedLevel(levels[0].level);
    setLevelRestored(true);
  }, [levels, selectedTournament, selectedLevel, levelRestored]);

  useEffect(() => {
    if (!selectionRestored) return;
    if (selectedTournament && !levelRestored) return;
    try {
      localStorage.setItem(
        MATCH_RESULTS_SELECTION_STORAGE_KEY,
        JSON.stringify({
          tournamentId: selectedTournament?.id || '',
          level: selectedLevel || '',
          searchQuery,
          selectedGroup,
          selectedStatus
        })
      );
    } catch (error) {
      console.error('Ошибка сохранения выбора результатов матчей:', error);
    }
  }, [
    selectedTournament,
    selectedLevel,
    searchQuery,
    selectedGroup,
    selectedStatus,
    selectionRestored,
    levelRestored
  ]);

  useEffect(() => {
    if (selectedTournament && selectedLevel) {
      fetchMatches();
      fetchBracketTemplate();
    }
  }, [selectedTournament, selectedLevel]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(MATCH_RESULTS_GROUPS_SEND_SELECTION_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as string[];
      if (!Array.isArray(parsed)) return;
      setSelectedGroupsAndTopicsForResult(new Set(parsed.filter((item) => typeof item === 'string')));
    } catch (error) {
      console.error('Ошибка восстановления выбранных групп для отправки результата:', error);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        MATCH_RESULTS_GROUPS_SEND_SELECTION_STORAGE_KEY,
        JSON.stringify(Array.from(selectedGroupsAndTopicsForResult))
      );
    } catch (error) {
      console.error('Ошибка сохранения выбранных групп для отправки результата:', error);
    }
  }, [selectedGroupsAndTopicsForResult]);

  const toDateInputValue = (isoDate?: string | null): string => {
    if (!isoDate) return '';
    return String(isoDate).slice(0, 10);
  };

  const fetchBracketTemplate = async () => {
    if (!selectedTournament || !selectedLevel) return;
    try {
      // Сначала загружаем данные турнира
      const tournament = await apiGet<any>(`/tournaments/${selectedTournament.id}`);
      
      let levelBrackets: Record<string, any> = {};
      
      if (tournament.levelBrackets) {
        try {
          levelBrackets = typeof tournament.levelBrackets === 'string'
            ? JSON.parse(tournament.levelBrackets)
            : tournament.levelBrackets;
        } catch (e) {
          console.error('[MatchResults] Ошибка парсинга levelBrackets:', e);
        }
      }

      const levelData = levelBrackets[selectedLevel];
      if (!levelData) {
        setLevelBracketFormat(null);
        setLevelPreScheduleSkeleton(false);
        setPlayoffStage(null);
        return;
      }

      const levelDataObject =
        levelData && typeof levelData === 'object'
          ? (levelData as Record<string, any>)
          : null;
      setLevelPreScheduleSkeleton(Boolean(levelDataObject?.preScheduleSkeleton));

      // Поддерживаем старый формат (просто templateId) и новый формат (объект)
      const templateId = typeof levelData === 'string' ? levelData : levelData.templateId;
      if (!templateId) {
        setLevelBracketFormat(null);
        setLevelPreScheduleSkeleton(false);
        setPlayoffStage(null);
        return;
      }

      // Теперь загружаем сам шаблон
      const template = await apiGet<any>(`/bracket-templates/${templateId}`);
      setLevelBracketFormat(template?.format || null);
      
      if (template && template.format === 'MIXED') {
        const structure = typeof template.structure === 'string' 
          ? JSON.parse(template.structure) 
          : template.structure;
        const playoffStageData = structure.stages.find((s: any) => s.type === 'SINGLE_ELIMINATION');
        
        if (playoffStageData) {
          let rounds: Array<{ round: number; name: string; matches: number }> = [];
          
          // Проверяем режим прямого финала
          if (playoffStageData.directFinal === true) {
            let roundNumber = 1;
            
            // Если включена игра за 3-е место, добавляем её первой
            if (playoffStageData.thirdPlaceMatch === true) {
              rounds.push({
                round: roundNumber,
                name: 'Матч за 3-е место',
                matches: 1
              });
              roundNumber++;
            }
            
            // Финал всегда есть
            rounds.push({
              round: roundNumber,
              name: 'Финал',
              matches: 1
            });
          }
          
          setPlayoffStage({
            rounds,
            directFinal: playoffStageData.directFinal,
            thirdPlaceMatch: playoffStageData.thirdPlaceMatch
          });
        }
      } else if (template && template.format === 'SINGLE_ELIMINATION') {
        const structure = typeof template.structure === 'string'
          ? JSON.parse(template.structure)
          : template.structure;

        const rounds = Array.isArray(structure?.rounds)
          ? structure.rounds
              .map((round: any) => ({
                round: Number(round?.round) || 0,
                name: String(round?.name || ''),
                matches: Number(round?.matches) || 0
              }))
              .filter((round: any) => round.matches > 0)
          : [];

        setPlayoffStage({
          rounds,
          directFinal: false,
          thirdPlaceMatch: false
        });
      } else {
        setPlayoffStage(null);
      }
    } catch (error) {
      console.error('[MatchResults] Ошибка загрузки шаблона сетки:', error);
      setLevelBracketFormat(null);
      setLevelPreScheduleSkeleton(false);
      setPlayoffStage(null);
    }
  };

  const fetchClubsAndCourts = async () => {
    try {
      const [clubsData, courtsData] = await Promise.all([
        apiGet<Club[] | { clubs?: Club[] }>('/clubs'),
        apiGet<Court[] | { items?: Court[] }>('/courts')
      ]);

      const normalizedClubs = Array.isArray(clubsData) ? clubsData : (clubsData.clubs || []);
      const normalizedCourts = Array.isArray(courtsData) ? courtsData : (courtsData.items || []);

      setClubs(Array.isArray(normalizedClubs) ? normalizedClubs : []);
      setCourts(Array.isArray(normalizedCourts) ? normalizedCourts : []);
    } catch (error) {
      console.error('Ошибка загрузки клубов/кортов:', error);
      setClubs([]);
      setCourts([]);
    }
  };

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const data = await apiGet<{ tournaments: Tournament[] } | Tournament[]>('/tournaments?limit=1000');
      if (Array.isArray(data)) {
        setTournaments(data);
      } else {
        setTournaments(Array.isArray(data.tournaments) ? data.tournaments : []);
      }
    } catch (error) {
      console.error('Ошибка загрузки турниров:', error);
      setTournaments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLevels = async () => {
    if (!selectedTournament) return;
    try {
      const tournament = await apiGet<any>(`/tournaments/${selectedTournament.id}`);
      const memberships = tournament.memberships || [];
      const levelMap = new Map<string, number>();
      
      memberships.forEach((m: any) => {
        if (m.level) {
          levelMap.set(m.level, (levelMap.get(m.level) || 0) + 1);
        }
      });

      const levelsList: TournamentLevel[] = Array.from(levelMap.entries())
        .map(([level, count]) => ({ level, playerCount: count }))
        .sort((a, b) => a.level.localeCompare(b.level));

      setLevels(levelsList);
    } catch (error) {
      console.error('Ошибка загрузки уровней:', error);
      setLevels([]);
    }
  };

  const fetchMatches = async () => {
    if (!selectedTournament || !selectedLevel) return;
    try {
      setLoading(true);
      const data = await apiGet<{ matches: Match[]; groups: any[] }>(
        `/matches/tournament/${selectedTournament.id}/level/${selectedLevel}`
      );
      const rawMatches = data.matches || [];
      const strictLevelMatches = rawMatches.filter((match) => {
        if (match.groupId) {
          return (match.group?.level || '').trim() === selectedLevel;
        }
        // Legacy-турниры могли быть сохранены без match.level в плей-офф.
        // В таком случае доверяем backend-фильтрации ветки уровня и не скрываем такие матчи.
        const playoffLevel = (match.level || '').trim();
        return !playoffLevel || playoffLevel === selectedLevel;
      });
      console.log('Загружено матчей:', rawMatches.length);
      console.log('После strict level-фильтра:', strictLevelMatches.length);
      setMatches(strictLevelMatches);
      setGroups(data.groups || []);
    } catch (error) {
      console.error('Ошибка загрузки матчей:', error);
      setMatches([]);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateMatches = async () => {
    if (!selectedTournament || !selectedLevel) return;
    try {
      setGenerating(true);
      const preview = await apiPost<GenerateMatchesPreviewResponse>(
        `/matches/generate/tournament/${selectedTournament.id}/level/${selectedLevel}`,
        { preview: true }
      );
      setGeneratePreviewData(preview || { matches: [] });
      setShowGeneratePreviewModal(true);
    } catch (error: any) {
      console.error('Ошибка генерации матчей:', error);
      
      // Формируем детальное сообщение об ошибке
      let errorMessage = `Ошибка генерации матчей: ${error.message || 'Неизвестная ошибка'}`;
      
      // Если есть детали из ответа сервера, добавляем их
      if (error.response?.data) {
        const errorData = error.response.data;
        errorMessage = `Ошибка генерации матчей:\n\n${errorData.message || errorData.error || 'Неизвестная ошибка'}`;
        
        if (errorData.name) {
          errorMessage += `\n\nТип ошибки: ${errorData.name}`;
        }
        
        if (errorData.code) {
          errorMessage += `\n\nКод ошибки: ${errorData.code}`;
        }
        
        if (errorData.stack) {
          errorMessage += `\n\nДетали:\n${errorData.stack}`;
        }
      }
      
      alert(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const handleConfirmGenerateMatches = async () => {
    if (!selectedTournament || !selectedLevel) return;
    try {
      setGenerating(true);
      const result = await apiPost<any>(
        `/matches/generate/tournament/${selectedTournament.id}/level/${selectedLevel}`,
        {}
      );
      if (typeof result?.created === 'number' && typeof result?.total === 'number') {
        alert(`Создано ${result.created} матчей из ${result.total} возможных. Существовало: ${result.existing ?? 0}`);
      } else if (result?.message) {
        alert(result.message);
      } else {
        alert('Генерация выполнена, но сервер вернул неожиданный формат ответа.');
      }
      setShowGeneratePreviewModal(false);
      setGeneratePreviewData(null);
      await fetchMatches();
    } catch (error: any) {
      console.error('Ошибка генерации матчей:', error);

      let errorMessage = `Ошибка генерации матчей: ${error.message || 'Неизвестная ошибка'}`;
      if (error.response?.data) {
        const errorData = error.response.data;
        errorMessage = `Ошибка генерации матчей:\n\n${errorData.message || errorData.error || 'Неизвестная ошибка'}`;
      }
      alert(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const handleGeneratePlayoffMatches = async () => {
    if (!selectedTournament || !selectedLevel) return;

    const hasExistingMatchesForLevel = matches.length > 0;
    const shouldWarnAboutFullReset =
      levelBracketFormat === 'SINGLE_ELIMINATION' && levelPreScheduleSkeleton && hasExistingMatchesForLevel;
    if (shouldWarnAboutFullReset) {
      const confirmed = window.confirm(
        `Внимание!\n\n` +
        `Сейчас уже существуют матчи уровня ${selectedLevel} в турнире "${selectedTournament.name}".\n` +
        `При нажатии "Сгенерировать сетку плей-оффа (каркас)" все данные по этому уровню будут полностью удалены и созданы заново ` +
        `(включая результаты, статусы и расписание матчей).\n\n` +
        `Продолжить?`
      );
      if (!confirmed) {
        return;
      }
    }

    try {
      setGenerating(true);
      const result = await apiPost<any>(
        `/matches/generate-playoff/tournament/${selectedTournament.id}/level/${selectedLevel}`,
        {}
      );
      console.log('Результат генерации матчей плей-оффа:', result);
      let message = `Создано ${result.created} матчей плей-оффа из ${result.total} возможных.`;
      if (result.debug) {
        message += `\n\nОтладочная информация:\n- Записей жеребьёвки: ${result.debug.playoffEntriesCount}\n- Матчей в первом раунде: ${result.debug.firstRoundMatches}\n- Матчей для создания: ${result.debug.matchesToCreateCount}\n- Создано: ${result.debug.createdCount}`;
        if (result.debug.groupPlaceKeys) {
          message += `\n- Ключи в маппинге: ${result.debug.groupPlaceKeys.join(', ')}`;
        }
        if (result.debug.logs && result.debug.logs.length > 0) {
          console.log('Логи генерации матчей плей-оффа:');
          result.debug.logs.forEach((log: string) => console.log(log));
          message += `\n\nПодробные логи выведены в консоль браузера (F12).`;
        }
      }
      alert(message);
      await fetchMatches();
    } catch (error: any) {
      console.error('Ошибка генерации матчей плей-оффа:', error);
      
      let errorMessage = `Ошибка генерации матчей плей-оффа: ${error.message || 'Неизвестная ошибка'}`;
      
      if (error.response?.data) {
        const errorData = error.response.data;
        errorMessage = errorData.error || errorData.message || errorMessage;
      }
      
      alert(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateNextPlayoffRound = async () => {
    if (!selectedTournament || !selectedLevel) return;
    try {
      setGeneratingNextRound(true);
      const result = await apiPost<any>(
        `/matches/generate-next-playoff-round/tournament/${selectedTournament.id}/level/${selectedLevel}`,
        {}
      );
      console.log('Результат генерации следующего раунда плей-оффа:', result);
      alert(result.message || `Создано ${result.created} матчей следующего раунда плей-оффа`);
      await fetchMatches();
    } catch (error: any) {
      console.error('Ошибка генерации следующего раунда плей-оффа:', error);
      const rawError = String(error?.response?.data?.error || error?.message || 'Неизвестная ошибка');
      const normalizedError = rawError.toLowerCase();
      if (normalizedError.includes('плей-офф уже завершен')) {
        alert('Плей-офф уже завершен.');
        await fetchMatches();
      } else {
        alert(`Ошибка: ${rawError}`);
      }
    } finally {
      setGeneratingNextRound(false);
    }
  };

  const handleRepairFirstPlayoffRound = async () => {
    if (!selectedTournament || !selectedLevel) return;

    const confirmed = window.confirm(
      `Восстановить отсутствующие матчи 1-го раунда для уровня ${selectedLevel}?\n\n` +
      `Существующие матчи, результаты и расписание не будут удалены. Будут созданы только отсутствующие Playoff-N.`
    );
    if (!confirmed) return;

    try {
      setRepairingFirstRound(true);
      const result = await apiPost<any>(
        `/matches/repair-first-playoff-round/tournament/${selectedTournament.id}/level/${selectedLevel}`,
        {}
      );
      alert(result.message || `Восстановлено ${result.created || 0} матчей первого раунда.`);
      await fetchMatches();
    } catch (error: any) {
      console.error('Ошибка восстановления первого раунда плей-оффа:', error);
      const rawError = String(error?.response?.data?.error || error?.message || 'Неизвестная ошибка');
      alert(`Ошибка: ${rawError}`);
    } finally {
      setRepairingFirstRound(false);
    }
  };

  const handleEditMatch = async (match: Match) => {
    setEditingMatch(match);
    setResultInput(match.score || '');
    
    // Загружаем список всех доступных форматов
    try {
      const formats = await apiGet<any[]>('/match-formats');
      setAvailableFormats(formats || []);
    } catch (error) {
      console.error('Ошибка загрузки форматов:', error);
      setAvailableFormats([]);
    }
    
    // Получаем полную информацию о матче с форматом
    try {
      const fullMatch = await apiGet<Match>(`/matches/${match.id}`);
      setEditingMatch(fullMatch);

      const hasReservation = !!fullMatch.reservation?.id;
      const matchDateValue = hasReservation
        ? toDateInputValue(fullMatch.reservation?.date)
        : toDateInputValue(fullMatch.scheduledDate || null);
      const matchTimeValue = hasReservation
        ? (fullMatch.reservation?.startTime || '')
        : (fullMatch.scheduledTime || '');
      setResultDate(matchDateValue);
      setResultTime(matchTimeValue);
      setResultDateTimeLocked(hasReservation);
      setResultClubId(fullMatch.court?.club?.id || fullMatch.reservation?.court?.club?.id || '');
      setResultCourtId(fullMatch.court?.id || fullMatch.reservation?.court?.id || '');
      
      // Если есть формат матча (загружен или нужно загрузить)
      let format = fullMatch.matchFormat;
      if (!format && fullMatch.matchFormatId) {
        format = await apiGet<any>(`/match-formats/${fullMatch.matchFormatId}`);
      }
      
      // Сохраняем формат для использования в парсинге результата
      const currentFormat = format || { maxSets: 3, setsToWin: 2, hasTiebreak: true, tiebreakTo: 7, superTiebreak: false, superTiebreakTo: 10 };
      setMatchFormat(currentFormat);
      
      // Инициализируем сеты на основе формата
      // Важно: используем именно maxSets из формата, без fallback на 3
      const maxSets = currentFormat.maxSets !== undefined ? currentFormat.maxSets : 3;
      const initialSets: SetScore[] = [];
      for (let i = 1; i <= maxSets; i++) {
        initialSets.push({ set: i, player1: 0, player2: 0, tiebreak: null });
      }
      setSets(initialSets);
      
      // Сбрасываем специальные статусы перед загрузкой результата
      setSpecialStatus({ type: null, player: null, reason: '' });
      setSuspensionStatusOption('SUSPENSION_OTHER');
      const initialAdministrativePlayers: Partial<Record<AdministrativeSlot, AdministrativePlayerFlags>> = {};
      ADMIN_SLOTS.forEach((slot) => {
        const playerIdKey = `${slot}Id` as keyof Match;
        if (!fullMatch[playerIdKey]) return;
        initialAdministrativePlayers[slot] = createDefaultAdministrativePlayerFlags();
      });
      setAdministrativePlayers(initialAdministrativePlayers);
      setAdministrativeExcludeFromElo(false);
      setAdministrativeSuppressTelegramResults(false);
      setAdministrativeReason('');
      setValidationErrors([]);
      
      // Парсим существующий результат, если есть
      if (fullMatch.result) {
        try {
          const result: MatchResult = JSON.parse(fullMatch.result);
          
          // Восстанавливаем сеты
          if (result.sets && result.sets.length > 0) {
            // Объединяем существующие сеты с инициализированными (на случай если формат изменился)
            const restoredSets: SetScore[] = [];
            for (let i = 1; i <= maxSets; i++) {
              const existingSet = result.sets.find(s => s.set === i);
              if (existingSet) {
                // Конвертируем старый формат тайбрейка (число) в новый (объект)
                let tiebreak = existingSet.tiebreak;
                if (tiebreak !== null && tiebreak !== undefined) {
                  if (typeof tiebreak === 'number') {
                    // Старый формат: только счет победителя
                    // Преобразуем в новый формат: полный счет
                    // Если счет 7:6, тайбрейк должен быть 7:X, где X < 7
                    // Если счет 6:7, тайбрейк должен быть X:7, где X < 7
                    if (existingSet.player1 === 7 && existingSet.player2 === 6) {
                      tiebreak = { player1: tiebreak, player2: tiebreak - 2 }; // Предполагаем минимальную разницу 2
                    } else if (existingSet.player1 === 6 && existingSet.player2 === 7) {
                      tiebreak = { player1: tiebreak - 2, player2: tiebreak }; // Предполагаем минимальную разницу 2
                    } else {
                      // Если счет не 7:6 или 6:7, используем как счет победителя
                      tiebreak = { player1: existingSet.player1 > existingSet.player2 ? tiebreak : tiebreak - 2, 
                                   player2: existingSet.player2 > existingSet.player1 ? tiebreak : tiebreak - 2 };
                    }
                  }
                }
                restoredSets.push({ ...existingSet, tiebreak });
              } else {
                restoredSets.push({ set: i, player1: 0, player2: 0, tiebreak: null });
              }
            }
            setSets(restoredSets);
          }
          
          // Восстанавливаем специальные статусы
          if (result.specialStatus) {
            setSpecialStatus({
              type: result.specialStatus.type,
              player: result.specialStatus.player || null,
              reason: result.specialStatus.reason || '',
              reasonCode: String(result.specialStatus.reasonCode || '').trim() || undefined
            });
            if (result.specialStatus.type === 'SUSPENSION') {
              setSuspensionStatusOption(
                inferSuspensionStatusOption(result.specialStatus.reason || '', result.specialStatus.reasonCode || '')
              );
            } else {
              setSuspensionStatusOption('SUSPENSION_OTHER');
            }
          }
          if (result.administrative) {
            const restoredAdministrativePlayers: Partial<Record<AdministrativeSlot, AdministrativePlayerFlags>> = { ...initialAdministrativePlayers };
            ADMIN_SLOTS.forEach((slot) => {
              const incoming = result.administrative?.players?.[slot];
              if (!incoming) return;
              restoredAdministrativePlayers[slot] = {
                ...createDefaultAdministrativePlayerFlags(),
                ...restoredAdministrativePlayers[slot],
                noShow: Boolean(incoming.noShow),
                excludeFromStats: Boolean(incoming.excludeFromStats),
                excludeFromRatingPoints: Boolean(incoming.excludeFromRatingPoints),
                cancelEntryFeeAccrual: Boolean(incoming.cancelEntryFeeAccrual)
              };
            });
            setAdministrativePlayers(restoredAdministrativePlayers);
            setAdministrativeExcludeFromElo(Boolean(result.administrative.excludeFromElo));
            setAdministrativeSuppressTelegramResults(Boolean(result.administrative.suppressTelegramResults));
            setAdministrativeReason(String(result.administrative.reason || ''));
          }
        } catch (e) {
          console.error('Ошибка парсинга результата:', e);
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки матча:', error);
      // Используем стандартный формат если не удалось загрузить
      setMatchFormat({ maxSets: 3, setsToWin: 2, hasTiebreak: true, tiebreakTo: 7 });
      setSets([
        { set: 1, player1: 0, player2: 0, tiebreak: null },
        { set: 2, player1: 0, player2: 0, tiebreak: null },
        { set: 3, player1: 0, player2: 0, tiebreak: null }
      ]);
      // Сбрасываем специальные статусы
      setSpecialStatus({ type: null, player: null, reason: '' });
      setSuspensionStatusOption('SUSPENSION_OTHER');
      setAdministrativePlayers({});
      setAdministrativeExcludeFromElo(false);
      setAdministrativeSuppressTelegramResults(false);
      setAdministrativeReason('');
      setValidationErrors([]);
    }
  };

  // Валидация результата
  const validateResult = (): string[] => {
    const errors: string[] = [];
    
    // Если выбран специальный статус, проверяем его
    if (specialStatus.type) {
      if (!specialStatus.player && (specialStatus.type === 'REFUSAL' || specialStatus.type === 'DISQUALIFICATION' || specialStatus.type === 'INJURY' || specialStatus.type === 'NO_SHOW')) {
        errors.push('Необходимо указать игрока для выбранного статуса');
      }
      // При специальном статусе разрешаем записывать незавершенный решающий тайбрейк
      // Он не будет учитываться в счете по сетам
      return errors;
    }
    
    // Проверяем, что хотя бы один сет заполнен
    const filledSets = sets.filter(s => s.player1 > 0 || s.player2 > 0);
    if (filledSets.length === 0) {
      errors.push('Необходимо заполнить хотя бы один сет');
      return errors;
    }
    
    // Валидация каждого заполненного сета
    filledSets.forEach((set, index) => {
      const isSuperTiebreak = set.superTiebreak === true;
      
      // Проверка отрицательных значений
      if (set.player1 < 0 || set.player2 < 0) {
        errors.push(`Сет ${set.set}: счет не может быть отрицательным`);
      }
      
      if (isSuperTiebreak) {
        // Валидация решающего тайбрейка
        const superTiebreakTo = matchFormat?.superTiebreakTo || 10;
        const diff = Math.abs(set.player1 - set.player2);
        const winner = Math.max(set.player1, set.player2);
        
        // Проверка ничьей
        if (set.player1 === set.player2) {
          errors.push(`Сет ${set.set}: решающий тайбрейк не может быть ничьим (${set.player1}-${set.player2})`);
        }
        
        // Победитель должен набрать минимум superTiebreakTo очков (обычно 10)
        if (winner > 0 && winner < superTiebreakTo) {
          errors.push(`Сет ${set.set}: для победы в решающем тайбрейке необходимо набрать минимум ${superTiebreakTo} очков`);
        }

        // После достижения superTiebreakTo нужна разница минимум 2 очка
        if (winner >= superTiebreakTo && diff < 2 && set.player1 > 0 && set.player2 > 0) {
          errors.push(`Сет ${set.set}: решающий тайбрейк должен быть выигран с разницей минимум в 2 очка (${set.player1}-${set.player2})`);
        }
      } else {
        // Валидация обычного сета
        // Проверка ничьей (кроме 6-6 с тайбрейком)
        if (set.player1 === set.player2 && set.player1 !== 6) {
          errors.push(`Сет ${set.set}: счет не может быть ничьим (${set.player1}-${set.player2})`);
        }
        
        // Проверка тайбрейка
        if ((set.player1 === 6 && set.player2 === 6) || (set.player1 === 7 && set.player2 === 6) || (set.player1 === 6 && set.player2 === 7)) {
          if (!matchFormat?.hasTiebreak) {
            errors.push(`Сет ${set.set}: формат не поддерживает тайбрейк`);
          } else if (!set.tiebreak || set.tiebreak === null) {
            errors.push(`Сет ${set.set}: при счете ${set.player1}-${set.player2} необходимо указать тайбрейк`);
          } else {
            // Валидация полного счета тайбрейка
            const tiebreak = set.tiebreak;
            if (typeof tiebreak === 'object' && tiebreak.player1 !== undefined) {
              const tbPlayer1 = tiebreak.player1;
              const tbPlayer2 = tiebreak.player2;
              const tbDiff = Math.abs(tbPlayer1 - tbPlayer2);
              const tbWinner = Math.max(tbPlayer1, tbPlayer2);
              const tiebreakTo = matchFormat?.tiebreakTo || 7;
              
              // Проверка минимального значения для победы
              if (tbWinner < tiebreakTo) {
                errors.push(`Сет ${set.set}: тайбрейк должен быть выигран минимум ${tiebreakTo} очками`);
              }
              
              // Проверка разницы минимум 2 очка
              if (tbDiff < 2) {
                errors.push(`Сет ${set.set}: тайбрейк должен быть выигран с разницей минимум в 2 очка (${tbPlayer1}-${tbPlayer2})`);
              }
              
              // Проверка, что победитель тайбрейка соответствует победителю сета
              const setWinner = set.player1 > set.player2 ? 'player1' : 'player2';
              const tbWinnerIs = tbPlayer1 > tbPlayer2 ? 'player1' : 'player2';
              if (set.player1 === 7 && set.player2 === 6 && tbWinnerIs !== 'player1') {
                errors.push(`Сет ${set.set}: победитель тайбрейка должен соответствовать победителю сета (7:6 требует победы в тайбрейке игрока 1)`);
              }
              if (set.player1 === 6 && set.player2 === 7 && tbWinnerIs !== 'player2') {
                errors.push(`Сет ${set.set}: победитель тайбрейка должен соответствовать победителю сета (6:7 требует победы в тайбрейке игрока 2)`);
              }
            } else if (typeof tiebreak === 'number') {
              // Старый формат - проверяем минимальное значение
              if (tiebreak < (matchFormat?.tiebreakTo || 7)) {
                errors.push(`Сет ${set.set}: тайбрейк должен быть выигран минимум ${matchFormat?.tiebreakTo || 7} очками`);
              }
            }
          }
        } else {
          // Если счет не 6-6, проверяем разницу в 2 гейма
          const diff = Math.abs(set.player1 - set.player2);
          if (diff < 2 && set.player1 > 0 && set.player2 > 0) {
            errors.push(`Сет ${set.set}: сет должен быть выигран с разницей минимум в 2 гейма (${set.player1}-${set.player2})`);
          }
          // Проверяем, что победитель набрал минимум 6 геймов
          const winner = Math.max(set.player1, set.player2);
          if (winner > 0 && winner < 6) {
            errors.push(`Сет ${set.set}: для победы в сете необходимо минимум 6 геймов`);
          }
        }
      }
    });
    
    // Проверяем, что матч завершен правильно (одна из сторон выиграла нужное количество сетов)
    if (matchFormat && filledSets.length > 0) {
      let player1Sets = 0;
      let player2Sets = 0;
      
      filledSets.forEach(set => {
        if (set.player1 > set.player2) {
          player1Sets++;
        } else if (set.player2 > set.player1) {
          player2Sets++;
        }
      });
      
      const setsToWin = matchFormat.setsToWin || 2;
      if (player1Sets < setsToWin && player2Sets < setsToWin) {
        errors.push(`Необходимо выиграть ${setsToWin} сетов для победы. Выиграно: ${player1Sets} и ${player2Sets}`);
      }
    }
    
    return errors;
  };

  const handleSaveResult = async (options?: { updateTelegramMessage?: boolean }) => {
    if (!editingMatch) return;
    if (savingResult) return;
    
    // Валидация
    setValidationErrors([]);
    const errors = validateResult();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Для спецстатуса при нулевом счете разрешаем фиксировать результат
    // без даты/времени/клуба/корта (для матчей без резерва).
    const filledSets = sets.filter(s => s.player1 > 0 || s.player2 > 0);
    const canSkipScheduleAndCourtForSpecialStatus = Boolean(specialStatus.type) && filledSets.length === 0;

    if (!canSkipScheduleAndCourtForSpecialStatus && !resultDateTimeLocked && (!resultDate || !resultTime)) {
      alert('Укажите дату и время матча (для матчей без резерва).');
      return;
    }

    if (!canSkipScheduleAndCourtForSpecialStatus && !resultDateTimeLocked && (!resultClubId || !resultCourtId)) {
      alert('Укажите клуб и корт матча (для матчей без резерва).');
      return;
    }
    
    try {
      setSavingResult(true);
      // Формируем результат в JSON формате
      let resultData: MatchResult;
      
      // Если выбран специальный статус
      if (specialStatus.type) {
        resultData = {
          sets: filledSets, // Сохраняем сеты вместе со статусом
          specialStatus: {
            type: specialStatus.type,
            player: specialStatus.player || undefined,
            reason: specialStatus.reason || undefined,
            reasonCode: specialStatus.type === 'SUSPENSION'
              ? (specialStatus.reasonCode || 'OTHER')
              : undefined
          },
          administrative: {
            players: administrativePlayers,
            excludeFromElo: administrativeExcludeFromElo,
            suppressTelegramResults: administrativeSuppressTelegramResults,
            reason: administrativeReason || undefined
          },
          completedAt: new Date().toISOString()
        };
      } else {
        // Обычный результат с сетами
        resultData = {
          sets: filledSets,
          administrative: {
            players: administrativePlayers,
            excludeFromElo: administrativeExcludeFromElo,
            suppressTelegramResults: administrativeSuppressTelegramResults,
            reason: administrativeReason || undefined
          },
          completedAt: new Date().toISOString()
        };
      }
      
      // Отправляем результат через специальный endpoint
      await apiPost(`/matches/${editingMatch.id}/result`, {
        result: resultData,
        matchFormatId: matchFormat?.id, // Обновляем формат матча, если он был изменен
        scheduledDate: resultDate ? `${resultDate}T00:00:00.000Z` : null,
        scheduledTime: resultTime || null,
        courtId: resultCourtId || null,
        updateTelegramMessage: options?.updateTelegramMessage === true
      });
      
      // Сбрасываем состояние
      setEditingMatch(null);
      setResultInput('');
      setSets([]);
      setSpecialStatus({ type: null, player: null, reason: '' });
      setSuspensionStatusOption('SUSPENSION_OTHER');
      setAdministrativePlayers({});
      setAdministrativeExcludeFromElo(false);
      setAdministrativeSuppressTelegramResults(false);
      setAdministrativeReason('');
      setWalkover(false);
      setRetired({ player: null, reason: '' });
      setValidationErrors([]);
      setMatchFormat(null);
      setResultDate('');
      setResultTime('');
      setResultDateTimeLocked(false);
      setResultClubId('');
      setResultCourtId('');
      
      await fetchMatches();
    } catch (error: any) {
      alert(`Ошибка сохранения результата: ${error.message || 'Неизвестная ошибка'}`);
    } finally {
      setSavingResult(false);
    }
  };

  const isCompletedLikeStatus = (status?: string): boolean =>
    ['COMPLETED', 'WALKOVER', 'RETIRED', 'CANCELLED'].includes(String(status || '').toUpperCase());

  type PlayoffPendingSide =
    | { type: 'lines'; lines: string[] }
    | { type: 'reference'; reference: string };

  const getPlayoffSourceSidePending = (targetMatch: Match, side: 'left' | 'right'): PlayoffPendingSide | null => {
    if (!targetMatch.matchCode?.startsWith('Playoff-') || !playoffStage?.rounds?.length) return null;

    const targetNumber = parsePlayoffMatchNumber(targetMatch.matchCode);
    if (targetNumber <= 0) return null;

    let currentRoundIndex = -1;
    let currentRoundStart = 1;
    let currentRoundSize = 0;
    let runningStart = 1;

    for (let i = 0; i < playoffStage.rounds.length; i++) {
      const size = Number(playoffStage.rounds[i]?.matches) || 0;
      if (size <= 0) continue;
      const end = runningStart + size - 1;
      if (targetNumber >= runningStart && targetNumber <= end) {
        currentRoundIndex = i;
        currentRoundStart = runningStart;
        currentRoundSize = size;
        break;
      }
      runningStart = end + 1;
    }

    if (currentRoundIndex <= 0 || currentRoundSize <= 0) return null;

    const prevRoundSize = Number(playoffStage.rounds[currentRoundIndex - 1]?.matches) || 0;
    if (prevRoundSize <= 0) return null;

    const prevRoundStart = currentRoundStart - prevRoundSize;
    const indexInCurrentRound = targetNumber - currentRoundStart;
    const sourceNumberBase = prevRoundStart + indexInCurrentRound * 2;
    const sourceMatchNumber = side === 'left' ? sourceNumberBase : sourceNumberBase + 1;
    const sourceMatchCode = `Playoff-${sourceMatchNumber}`;
    const sourceMatch = matches.find((m) => m.matchCode === sourceMatchCode);
    if (!sourceMatch || isCompletedLikeStatus(sourceMatch.status)) return null;

    const sourceRoundLabel = sourceMatch.round?.name || getPlayoffRoundMeta(sourceMatch.matchCode).label || '-';
    const sourceLevelLabel = sourceMatch.level || targetMatch.level || '-';
    const sourceStageNumber =
      typeof sourceMatch.stageMatchNumber === 'number' && sourceMatch.stageMatchNumber > 0
        ? sourceMatch.stageMatchNumber
        : '-';

    if (selectedTournament?.format === 'DOUBLES') {
      const hasTeam1 = Boolean(sourceMatch.player1?.name && sourceMatch.player3?.name);
      const hasTeam2 = Boolean(sourceMatch.player2?.name && sourceMatch.player4?.name);
      if (!hasTeam1 || !hasTeam2) {
        return { type: 'reference', reference: `${sourceLevelLabel} • ${sourceRoundLabel} • ${sourceStageNumber}` };
      }
      const team1 = [sourceMatch.player1?.name, sourceMatch.player3?.name].filter(Boolean).join(' / ');
      const team2 = [sourceMatch.player2?.name, sourceMatch.player4?.name].filter(Boolean).join(' / ');
      const lines = [team1, team2].filter(Boolean);
      return lines.length > 0 ? { type: 'lines', lines } : null;
    }

    if (!sourceMatch.player1?.name || !sourceMatch.player2?.name) {
      return { type: 'reference', reference: `${sourceLevelLabel} • ${sourceRoundLabel} • ${sourceStageNumber}` };
    }

    const lines = [sourceMatch.player1?.name, sourceMatch.player2?.name].filter(Boolean) as string[];
    return lines.length > 0 ? { type: 'lines', lines } : null;
  };

  const getPlayerNames = (match: Match): string => {

    // Проверяем, является ли турнир парным
    const isDoubles = selectedTournament?.format === 'DOUBLES';
    const isPlayoffMatch = Boolean(match.matchCode?.startsWith('Playoff-'));
    
    if (isDoubles) {
      // Для парных турниров показываем команды и BYE как X
      const hasTeam1 = Boolean(match.player1?.name && match.player3?.name);
      const hasTeam2 = Boolean(match.player2?.name && match.player4?.name);
      const pendingLeft = !hasTeam1 && isPlayoffMatch ? getPlayoffSourceSidePending(match, 'left') : null;
      const pendingRight = !hasTeam2 && isPlayoffMatch ? getPlayoffSourceSidePending(match, 'right') : null;
      const team1 = hasTeam1
        ? `${match.player1!.name} / ${match.player3!.name}`
        : (
            (pendingLeft?.type === 'lines' ? pendingLeft.lines.join('\n') : pendingLeft?.reference) ||
            (isPlayoffMatch ? 'X' : '')
          );
      const team2 = hasTeam2
        ? `${match.player2!.name} / ${match.player4!.name}`
        : (
            (pendingRight?.type === 'lines' ? pendingRight.lines.join('\n') : pendingRight?.reference) ||
            (isPlayoffMatch ? 'X' : '')
          );
      if (!team1 && !team2) return '';
      return `${team1} vs ${team2}`;
    }

    // Для одиночных матчей плей-офф фиксируем явный BYE как X
    if (isPlayoffMatch) {
      const leftPending = getPlayoffSourceSidePending(match, 'left');
      const rightPending = getPlayoffSourceSidePending(match, 'right');
      const left = match.player1?.name ||
        (leftPending?.type === 'lines' ? leftPending.lines.join('\n') : leftPending?.reference) ||
        'X';
      const right = match.player2?.name ||
        (rightPending?.type === 'lines' ? rightPending.lines.join('\n') : rightPending?.reference) ||
        'X';
      return `${left} / ${right}`;
    }

    // Для остальных одиночных матчей старый формат
    const names: string[] = [];
    if (match.player1) names.push(match.player1.name);
    if (match.player2) names.push(match.player2.name);
    if (match.player3) names.push(match.player3.name);
    if (match.player4) names.push(match.player4.name);
    return names.join(' / ');
  };

  const renderPlayersCell = (match: Match): React.ReactNode => {
    const isPlayoffMatch = Boolean(match.matchCode?.startsWith('Playoff-'));
    if (!isPlayoffMatch) return getPlayerNames(match);

    const isDoubles = selectedTournament?.format === 'DOUBLES';
    const leftPending = getPlayoffSourceSidePending(match, 'left');
    const rightPending = getPlayoffSourceSidePending(match, 'right');

    if (!leftPending && !rightPending) {
      return getPlayerNames(match);
    }

    const leftLabel = isDoubles
      ? (match.player1?.name && match.player3?.name ? `${match.player1.name} / ${match.player3.name}` : 'X')
      : (match.player1?.name || 'X');
    const rightLabel = isDoubles
      ? (match.player2?.name && match.player4?.name ? `${match.player2.name} / ${match.player4.name}` : 'X')
      : (match.player2?.name || 'X');

    const renderSide = (label: string, pending: PlayoffPendingSide | null) => {
      if (!pending) return <span className="inline-flex items-center min-h-[40px]">{label}</span>;
      return (
        <div className="inline-flex min-h-[40px] flex-col justify-center rounded border border-blue-300 bg-blue-50 px-2 py-1 leading-snug">
          {pending.type === 'reference'
            ? <span>{pending.reference}</span>
            : pending.lines.map((line, idx) => (
                <span key={`${line}-${idx}`}>{line}</span>
              ))}
        </div>
      );
    };

    return (
      <div className="flex items-center gap-2">
        {renderSide(leftLabel, leftPending)}
        <span className="inline-flex min-h-[40px] items-center text-gray-400">/</span>
        {renderSide(rightLabel, rightPending)}
      </div>
    );
  };

  // Функция для формирования текста результата матча
  const formatMatchResultText = (match: Match): string => {
    if (!match.result) return '';
    
    try {
      const result: MatchResult = JSON.parse(match.result);
      
      // Формируем заголовок результата: этап • уровень • группа
      const levelLabel = (match.level || match.group?.level || '').trim();
      const rawGroupName = (match.group?.name || '').trim();
      const groupLabel = rawGroupName.replace(/^(группа|group)\s*/i, '').trim() || rawGroupName;
      const stageLabel = match.groupId ? 'Etapa grupelor' : (match.round?.name || 'Etapa');
      
      // Проверяем, является ли турнир парным
      const isDoubles = selectedTournament?.format === 'DOUBLES';
      
      // Получаем имена игроков/команд
      let player1Name = '';
      let player2Name = '';
      
      if (isDoubles && match.player3 && match.player4) {
        // Для парных турниров формируем команды
        const team1 = [match.player1?.name, match.player3?.name].filter(Boolean).join(' / ');
        const team2 = [match.player2?.name, match.player4?.name].filter(Boolean).join(' / ');
        player1Name = team1;
        player2Name = team2;
      } else {
        // Для одиночных турниров
        player1Name = match.player1?.name || '';
        player2Name = match.player2?.name || '';
      }
      
      // Формируем счет
      let setsScore = '';
      let gamesScore = '';
      let isPlayer2Winner = false;
      
      // ВАЖНО: Для специальных статусов определяем победителя СРАЗУ по winnerId
      // Это должно быть ДО проверки сетов, т.к. у "Неявки" сетов может не быть!
      if (result.specialStatus && (result.specialStatus.type === 'NO_SHOW' || 
                                   result.specialStatus.type === 'REFUSAL' ||
                                   result.specialStatus.type === 'DISQUALIFICATION' ||
                                   result.specialStatus.type === 'INJURY')) {
        // Используем winnerId для определения победителя
        if (isDoubles) {
          // Для парного разряда проверяем оба ID команды
          if (match.winnerId === match.player1Id || match.winnerId === match.player3Id) {
            isPlayer2Winner = false;
          } else if (match.winnerId === match.player2Id || match.winnerId === match.player4Id) {
            isPlayer2Winner = true;
          }
        } else {
          // Для одиночного разряда
          isPlayer2Winner = match.winnerId === match.player2Id;
        }
      }
      
      if (result.sets && result.sets.length > 0) {
        // Подсчитываем общий счет по сетам
        let player1Sets = 0;
        let player2Sets = 0;
        
        // Проверяем, есть ли специальный статус (матч не завершен)
        const hasSpecialStatus = !!result.specialStatus;
        
        // Подсчитываем завершенные сеты
        result.sets.forEach((set) => {
          const hasTiebreak = set.tiebreak !== null && set.tiebreak !== undefined;
          const isSuperTiebreak = set.superTiebreak === true;
          
          // Определяем, завершен ли сет
          let isSetCompleted = false;
          
          if (isSuperTiebreak) {
            // Для решающего тайбрейка проверяем, завершен ли он
            if (hasTiebreak && set.tiebreak !== null) {
              if (typeof set.tiebreak === 'object' && 'player1' in set.tiebreak && 'player2' in set.tiebreak) {
                const { player1: tb1, player2: tb2 } = set.tiebreak;
                const maxScore = Math.max(tb1, tb2);
                const minScore = Math.min(tb1, tb2);
                isSetCompleted = maxScore >= 10 && (maxScore - minScore) >= 2;
              } else if (typeof set.tiebreak === 'number') {
                isSetCompleted = set.tiebreak >= 10;
              }
            } else {
              const maxScore = Math.max(set.player1, set.player2);
              const minScore = Math.min(set.player1, set.player2);
              isSetCompleted = maxScore >= 10 && (maxScore - minScore) >= 2;
            }
          } else if (hasTiebreak) {
            // Обычный тайбрейк - считаем завершенным, если есть тайбрейк
            isSetCompleted = true;
          } else {
            // Обычный сет - завершен, если один из игроков выиграл
            if (set.player1 > set.player2) {
              isSetCompleted = (set.player1 >= 6 && (set.player1 - set.player2) >= 2) || set.player1 === 7;
            } else if (set.player2 > set.player1) {
              isSetCompleted = (set.player2 >= 6 && (set.player2 - set.player1) >= 2) || set.player2 === 7;
            }
          }
          
          // Если есть специальный статус, считаем только завершенные сеты
          // Если специального статуса нет, считаем все сеты (матч завершен)
          if (!hasSpecialStatus || isSetCompleted) {
            // Определяем победителя сета с учетом тайбрейка
            let setWinner: 'player1' | 'player2' | null = null;
            
            if (hasTiebreak && set.tiebreak !== null) {
              // Если есть тайбрейк, победитель определяется по тайбрейку
              if (typeof set.tiebreak === 'object' && 'player1' in set.tiebreak && 'player2' in set.tiebreak) {
                const { player1: tb1, player2: tb2 } = set.tiebreak;
                if (tb1 > tb2) {
                  setWinner = 'player1';
                } else if (tb2 > tb1) {
                  setWinner = 'player2';
                }
              } else if (typeof set.tiebreak === 'number') {
                // Старый формат: тайбрейк - это счет победителя
                // Для супер-тайбрейка (10:7) или обычного тайбрейка (7:6)
                if (isSuperTiebreak) {
                  // Для супер-тайбрейка победитель определяется по счету геймов (очков)
                  if (set.player1 > set.player2) {
                    setWinner = 'player1';
                  } else if (set.player2 > set.player1) {
                    setWinner = 'player2';
                  }
                } else {
                  // Для обычного тайбрейка при счете 7:6 или 6:7
                  if (set.player1 === 7 && set.player2 === 6) {
                    setWinner = 'player1';
                  } else if (set.player1 === 6 && set.player2 === 7) {
                    setWinner = 'player2';
                  }
                }
              }
            } else {
              // Если тайбрейка нет, победитель определяется по счету геймов
              if (set.player1 > set.player2) {
                setWinner = 'player1';
              } else if (set.player2 > set.player1) {
                setWinner = 'player2';
              }
            }
            
            if (setWinner === 'player1') {
              player1Sets++;
            } else if (setWinner === 'player2') {
              player2Sets++;
            }
          }
        });
        
        // Определяем победителя
        // Для обычных матчей (БЕЗ специальных статусов) определяем по сетам
        if (!result.specialStatus) {
          isPlayer2Winner = player2Sets > player1Sets;
        }
        // Для специальных статусов победитель уже определен выше по winnerId
        
        // Если победитель - player2, переставляем счет сетов
        if (isPlayer2Winner) {
          [player1Sets, player2Sets] = [player2Sets, player1Sets];
        }
        
        setsScore = `${player1Sets}:${player2Sets}`;
        
        // Формируем строку счета каждого сета
        const setsScores = result.sets.map(set => {
          let setScore = '';
          
          // Для супер-тайбрейка не добавляем скобки с тайбрейком
          const isSuperTiebreak = shouldRenderAsSuperTiebreak(set, match.matchFormat?.superTiebreakTo || 10);
          
          // Если победитель - player2, переворачиваем счет сета
          if (isPlayer2Winner) {
            if (isSuperTiebreak) {
              setScore = `${set.player2}:${set.player1}`;
            } else if (set.tiebreak !== null && set.tiebreak !== undefined) {
              if (typeof set.tiebreak === 'object' && set.tiebreak.player1 !== undefined) {
                const loserScore = Math.min(set.tiebreak.player1, set.tiebreak.player2);
                // Переворачиваем счет: player2:player1
                setScore = `${set.player2}:${set.player1}(${loserScore})`;
              } else if (typeof set.tiebreak === 'number') {
                // Переворачиваем счет: player2:player1
                setScore = `${set.player2}:${set.player1}(${set.tiebreak})`;
              }
            } else {
              // Обычный сет, переворачиваем счет
              setScore = `${set.player2}:${set.player1}`;
            }
          } else {
            // Победитель - player1, оставляем как есть
            if (isSuperTiebreak) {
              setScore = `${set.player1}:${set.player2}`;
            } else if (set.tiebreak !== null && set.tiebreak !== undefined) {
              if (typeof set.tiebreak === 'object' && set.tiebreak.player1 !== undefined) {
                const loserScore = Math.min(set.tiebreak.player1, set.tiebreak.player2);
                setScore = `${set.player1}:${set.player2}(${loserScore})`;
              } else if (typeof set.tiebreak === 'number') {
                setScore = `${set.player1}:${set.player2}(${set.tiebreak})`;
              }
            } else {
              setScore = `${set.player1}:${set.player2}`;
            }
          }
          
          return setScore;
        }).join(' ');
        
        gamesScore = `(${setsScores})`;
      }
      
      // ВАЖНО: Перестановка имен должна быть ПОСЛЕ обработки сетов
      // чтобы работать и для матчей БЕЗ сетов (например, "Неявка")
      if (isPlayer2Winner) {
        [player1Name, player2Name] = [player2Name, player1Name];
      }
      
      const tournamentName = (selectedTournament?.name || '').trim();
      const header = [levelLabel, stageLabel, groupLabel].filter(Boolean).join(' • ');

      // Формируем текст (победитель всегда первый)
      let resultText = [tournamentName, header, `${player1Name} - ${player2Name}`]
        .filter(Boolean)
        .join('\n');
      
      // Обрабатываем специальные статусы
      if (result.specialStatus) {
        const label = SPECIAL_STATUS_LABELS[result.specialStatus.type] || result.specialStatus.type;
        let statusString = label;
        if (result.specialStatus.reason) {
          statusString = `${label} (${result.specialStatus.reason})`;
        }
        
        if (AUTO_SPECIAL_STATUS_TYPES.has(result.specialStatus.type)) {
          // В тексте победитель уже идет первым. Доводим победителя до нужного числа сетов.
          const setsToWin = match.matchFormat?.setsToWin || 2;
          const shortStatus =
            result.specialStatus.type === 'NO_SHOW' ||
            result.specialStatus.type === 'INJURY' ||
            result.specialStatus.type === 'REFUSAL'
              ? 'W.O.'
              : label.toLowerCase();
          let winnerSets = 0;
          let loserSets = 0;

          if (setsScore && /^\d+:\d+$/.test(setsScore)) {
            const [w, l] = setsScore.split(':').map(Number);
            winnerSets = Number.isFinite(w) ? w : 0;
            loserSets = Number.isFinite(l) ? l : 0;
          }

          winnerSets = Math.max(winnerSets, setsToWin);

          let gamesWithStatus = `(${shortStatus})`;
          if (gamesScore) {
            const cleanedGames = gamesScore.replace(/^\(/, '').replace(/\)$/, '');
            gamesWithStatus = `(${cleanedGames} ${shortStatus})`;
          }

          resultText += `\n${winnerSets}:${loserSets} ${gamesWithStatus}`;
        } else {
          // Для остальных спецстатусов сохраняем старое поведение.
          if (setsScore && setsScore !== '0:0') {
            resultText += `\n${setsScore} ${gamesScore}`;
          }
          resultText += `\n${statusString}`;
        }
      } else {
        // Для обычных матчей добавляем счет
        resultText += `\n${setsScore} ${gamesScore}`;
      }
      
      // Обрабатываем walkover (для обратной совместимости)
      if (result.walkover) {
        resultText += '\nW.O.';
      }
      
      // Обрабатываем retired (для обратной совместимости)
      if (result.retired) {
        const retiredString = result.retired.reason 
          ? `Retired: ${result.retired.reason}`
          : 'Retired';
        resultText += `\n${retiredString}`;
      }
      
      return resultText;
    } catch (error) {
      console.error('Ошибка форматирования результата:', error);
      return '';
    }
  };

  // Функция для копирования счета в буфер обмена
  const buildYoutubeResultText = (match: Match): string => {
    const base = formatMatchResultText(match);
    if (!base) return '';
    const lines = base
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    if (lines.length < 4) {
      return base.replace(/\s*\n+\s*/g, ' • ').replace(/\s{2,}/g, ' ').trim();
    }
    const head = lines.slice(0, 3);
    const tail = lines.slice(3).join(' ');
    return [...head, tail].join(' • ').replace(/\s{2,}/g, ' ').trim();
  };

  const copyTextToClipboard = async (textToCopy: string, successTitle: string) => {
    if (!textToCopy) {
      alert('Ошибка при форматировании результата');
      return;
    }
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      alert(`${successTitle}\n\n${textToCopy}`);
    } catch (clipboardError) {
      // Fallback для старых браузеров
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        alert(`${successTitle}\n\n${textToCopy}`);
      } catch (err) {
        alert(`Не удалось скопировать в буфер обмена. Текст:\n\n${textToCopy}`);
      }
      document.body.removeChild(textArea);
    }
  };

  const copyScoreToClipboard = async (match: Match) => {
    const textToCopy = formatMatchResultText(match);
    await copyTextToClipboard(textToCopy, 'Счет скопирован в буфер обмена:');
  };

  const copyYoutubeScoreToClipboard = async (match: Match) => {
    const textToCopy = buildYoutubeResultText(match);
    await copyTextToClipboard(textToCopy, 'Youtube-формат скопирован в буфер обмена:');
  };

  // Загрузка списка групп для отправки результата
  const fetchGroupsForResult = async () => {
    try {
      setLoadingGroups(true);
      
      // Получаем все группы
      const groups = await apiGet<Array<{
        chatId: string;
        title: string | null;
        username: string | null;
      }>>('/telegram-group-settings');
      
      // Для каждой группы получаем топики с задержкой между запросами
      // чтобы избежать превышения rate limit
      const groupsWithTopics: any[] = [];
      for (let i = 0; i < groups.length; i++) {
        const group = groups[i];
        try {
          // Добавляем задержку между запросами (кроме первого)
          if (i > 0) {
            await new Promise(resolve => setTimeout(resolve, 200)); // 200ms задержка
          }
          
          const topicsResponse = await apiGet<{
            topics: Array<{
              id: string;
              threadId: string;
              name: string | null;
            }>;
            mainThreadUnreadCount: number;
          }>(`/telegram-group-settings/${group.chatId}/topics`);
          
          groupsWithTopics.push({
            ...group,
            topics: topicsResponse.topics || []
          });
        } catch (error) {
          console.error(`Ошибка загрузки топиков для группы ${group.chatId}:`, error);
          groupsWithTopics.push({
            ...group,
            topics: []
          });
        }
      }
      
      setAllGroups(groupsWithTopics);
    } catch (error) {
      console.error('Ошибка загрузки групп:', error);
      alert('Ошибка загрузки списка групп');
    } finally {
      setLoadingGroups(false);
    }
  };

  // Обработчик открытия модального окна
  useEffect(() => {
    if (showSendResultModal && matchForSend) {
      if (sendResultTab === 'groups') {
        fetchGroupsForResult();
      }
    }
  }, [showSendResultModal, sendResultTab]);

  // Обработчик отправки результата
  const handleSendResult = async () => {
    if (!matchForSend) return;

    const resultText = formatMatchResultText(matchForSend);
    if (!resultText) {
      alert('Ошибка при форматировании результата');
      return;
    }

    try {
      setSendingResult(true);

      if (sendResultTab === 'copy') {
        // Копируем в буфер обмена
        await copyScoreToClipboard(matchForSend);
        setShowSendResultModal(false);
        setMatchForSend(null);
      } else if (sendResultTab === 'groups') {
        if (selectedGroupsAndTopicsForResult.size === 0) {
          alert('Выберите хотя бы одну группу или топик для отправки результата');
          return;
        }

        // Разделяем на группы и топики
        const groups: string[] = [];
        const topics: Array<{ chatId: string; threadId: string }> = [];
        
        selectedGroupsAndTopicsForResult.forEach(key => {
          if (key.includes(':')) {
            // Это топик
            const [chatId, threadId] = key.split(':');
            topics.push({ chatId, threadId });
          } else {
            // Это группа (основной поток)
            groups.push(key);
          }
        });

        const result = await apiPost('/matches/send-result-to-groups', {
          matchId: matchForSend.id,
          groups,
          topics
        });

        alert(result.message || `Результат отправлен в ${groups.length + topics.length} групп/топиков`);
        setShowSendResultModal(false);
        setMatchForSend(null);
      }
    } catch (error: any) {
      console.error('Ошибка отправки результата:', error);
      alert('Ошибка отправки результата: ' + (error.response?.data?.error || error.message));
    } finally {
      setSendingResult(false);
    }
  };

  const handleDeleteResult = async () => {
    if (!editingMatch) return;
    
    // Подтверждение удаления
    const playerNames = getPlayerNames(editingMatch);
    const confirmed = window.confirm(
      `Вы уверены, что хотите удалить результат матча?\n\n${playerNames}\n\nЭто действие нельзя отменить.`
    );
    
    if (!confirmed) return;
    
    try {
      await apiDelete(`/matches/${editingMatch.id}/result`);
      
      // Сбрасываем состояние
      setEditingMatch(null);
      setResultInput('');
      setSets([]);
      setSpecialStatus({ type: null, player: null, reason: '' });
      setSuspensionStatusOption('SUSPENSION_OTHER');
      setWalkover(false);
      setRetired({ player: null, reason: '' });
      setValidationErrors([]);
      setMatchFormat(null);
      
      await fetchMatches();
    } catch (error: any) {
      alert(`Ошибка удаления результата: ${error.message || 'Неизвестная ошибка'}`);
    }
  };

  const handleDeleteMatch = async (match: Match) => {
    const playerNames = getPlayerNames(match);
    const confirmed = window.confirm(
      `Вы уверены, что хотите удалить матч?\n\n${playerNames}\n\nЭто действие нельзя отменить.`
    );
    
    if (!confirmed) return;
    
    try {
      await apiDelete(`/matches/${match.id}`);
      await fetchMatches();
    } catch (error: any) {
      alert(`Ошибка удаления матча: ${error.message || 'Неизвестная ошибка'}`);
    }
  };

  const handleOpenStageEditor = (match: Match) => {
    const roundMap = new Map<string, RoundOption>();
    for (const item of matches) {
      if (!item.round?.id) continue;
      const id = String(item.round.id).trim();
      if (!id) continue;
      if (roundMap.has(id)) continue;
      roundMap.set(id, {
        id,
        name: String(item.round.name || '').trim() || id,
        order: typeof item.round.order === 'number' ? item.round.order : Number.MAX_SAFE_INTEGER
      });
    }
    const roundOptions = Array.from(roundMap.values()).sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.name.localeCompare(b.name, 'ru');
    });

    const currentNumber = match.groupId
      ? (typeof match.matchNumber === 'number' && Number.isFinite(match.matchNumber) ? match.matchNumber : null)
      : (typeof match.stageMatchNumber === 'number' && Number.isFinite(match.stageMatchNumber) ? match.stageMatchNumber : null);

    setEditingStageRoundOptions(roundOptions);
    setEditingStageMatch(match);
    setEditingStageLevel(String(match.level || selectedLevel || '').trim());
    setEditingStageRoundId(String(match.round?.id || '').trim());
    setEditingStageRoundName(String(match.round?.name || '').trim());
    setEditingStageNumber(currentNumber != null && currentNumber > 0 ? String(currentNumber) : '');
  };

  const normalizeStageNameForPreset = (value: string): string => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/ё/g, 'е');

  const pickStagePreset = (preset: 'FINAL' | 'SEMI' | 'QUARTER' | 'EIGHTH' | 'SIXTEENTH' | 'THIRTY_SECOND' | 'SIXTY_FOURTH' | 'THIRD_PLACE') => {
    const presetRoundNames: Record<typeof preset, string> = {
      FINAL: 'Финал',
      SEMI: '1/2',
      QUARTER: '1/4',
      EIGHTH: '1/8',
      SIXTEENTH: '1/16',
      THIRTY_SECOND: '1/32',
      SIXTY_FOURTH: '1/64',
      THIRD_PLACE: 'Матч за 3-е место'
    };

    const predicates: Record<typeof preset, (name: string) => boolean> = {
      FINAL: (name) => (name.includes('финал') && !name.includes('полуфин')) || name.includes('finala') || name === 'final',
      SEMI: (name) => name.includes('полуфин') || name.includes('semif'),
      QUARTER: (name) => name.includes('четверть') || name.includes('sfert'),
      EIGHTH: (name) => name.includes('1/8') || name.includes('optimi'),
      SIXTEENTH: (name) => name.includes('1/16') || name.includes('şaisprezecimi') || name.includes('saisprezecimi'),
      THIRTY_SECOND: (name) => name.includes('1/32') || name.includes('treizecidoimi') || name.includes('treizeci-doimi'),
      SIXTY_FOURTH: (name) => name.includes('1/64') || name.includes('saizecipatrimi') || name.includes('saizeci-patrimi'),
      THIRD_PLACE: (name) => name.includes('3') || name.includes('треть') || name.includes('locul 3')
    };

    const predicate = predicates[preset];
    const candidate = editingStageRoundOptions.find((option) => {
      const normalized = normalizeStageNameForPreset(option.name);
      return predicate(normalized);
    });
    if (candidate) {
      setEditingStageRoundId(candidate.id);
      setEditingStageRoundName(candidate.name);
    } else {
      setEditingStageRoundId('');
      setEditingStageRoundName(presetRoundNames[preset]);
    }
  };

  const handleSaveStageMeta = async () => {
    if (!editingStageMatch) return;
    const rawNumber = editingStageNumber.trim();
    const parsedNumber = rawNumber === '' ? null : Number(rawNumber);
    if (rawNumber !== '' && (!Number.isInteger(parsedNumber) || Number(parsedNumber) <= 0)) {
      alert('N должен быть целым числом больше 0');
      return;
    }

    const payload: any = {
      level: editingStageLevel.trim() || null,
      roundId: editingStageRoundId || null
    };
    if (!editingStageRoundId && editingStageRoundName.trim()) {
      payload.roundName = editingStageRoundName.trim();
    }
    if (editingStageMatch.groupId) {
      payload.matchNumber = parsedNumber;
    } else {
      payload.stageMatchNumber = parsedNumber;
    }

    try {
      setSavingStageMeta(true);
      await apiPut(`/matches/${editingStageMatch.id}`, payload);
      setEditingStageMatch(null);
      await fetchMatches();
    } catch (error: any) {
      alert(`Ошибка сохранения метаданных матча: ${error.message || 'Неизвестная ошибка'}`);
    } finally {
      setSavingStageMeta(false);
    }
  };

  const normalizePlayoffRoundName = (name: string): string => {
    const trimmed = String(name || '').trim();
    const map: Record<string, string> = {
      'Finala': 'Финал',
      'Semifinale': 'Полуфинал',
      'Sferturi de finală': 'Четвертьфинал',
      'Матч за 3-е место': 'Матч за 3-е место'
    };
    return map[trimmed] || trimmed;
  };

  const parsePlayoffMatchNumber = (matchCode: string | null | undefined): number => {
    const value = String(matchCode || '').trim();
    const latin = value.match(/^playoff-(\d+)$/i);
    if (latin?.[1]) return parseInt(latin[1], 10) || 0;
    const cyr = value.match(/^плей-офф\s*-\s*(\d+)$/i);
    if (cyr?.[1]) return parseInt(cyr[1], 10) || 0;
    return 0;
  };

  const getPlayoffRoundMeta = (matchCode: string | null | undefined): { order: number; label: string | null } => {
    const matchNumber = parsePlayoffMatchNumber(matchCode);
    if (matchNumber <= 0) return { order: 0, label: null };

    // Для directFinal сохраняем текущую бизнес-логику.
    if (playoffStage?.directFinal && playoffStage.rounds.length > 0) {
      if (playoffStage.thirdPlaceMatch) {
        if (matchNumber === 1) return { order: 1, label: 'Финал' };
        if (matchNumber === 2) return { order: 1, label: 'Матч за 3-е место' };
      } else if (matchNumber === 1) {
        return { order: 1, label: 'Финал' };
      }
      return { order: 1, label: 'Плей-офф' };
    }

    // Основной путь: определяем этап по шаблону (rounds), а не по числу созданных матчей.
    if (playoffStage?.rounds && playoffStage.rounds.length > 0) {
      let start = 1;
      for (let idx = 0; idx < playoffStage.rounds.length; idx++) {
        const round = playoffStage.rounds[idx];
        const matchesInRound = Number(round?.matches) || 0;
        if (matchesInRound <= 0) continue;
        const end = start + matchesInRound - 1;
        if (matchNumber >= start && matchNumber <= end) {
          return { order: idx + 1, label: normalizePlayoffRoundName(round?.name || 'Плей-офф') };
        }
        start = end + 1;
      }
    }

    // Fallback: если структура не загрузилась.
    return { order: matchNumber, label: 'Плей-офф' };
  };

  const filteredMatches = matches.filter(match => {
    // Фильтр по поиску
    if (searchQuery) {
      const playerNames = getPlayerNames(match).toLowerCase();
      if (!playerNames.includes(searchQuery.toLowerCase())) {
        return false;
      }
    }
    
    // Фильтр по группе (матчи плей-оффа без группы пропускаем этот фильтр)
    if (selectedGroup !== 'all' && match.groupId && match.groupId !== selectedGroup) {
      return false;
    }
    
    // Если выбрана конкретная группа, не показываем матчи плей-оффа (без группы)
    if (selectedGroup !== 'all' && !match.groupId) {
      return false;
    }
    
    // Фильтр по статусу
    if (selectedStatus !== 'all' && match.status !== selectedStatus) {
      return false;
    }
    
    return true;
  }).sort((a, b) => {
    // Сначала групповые матчи, затем плей-офф
    if (a.groupId && !b.groupId) return -1;
    if (!a.groupId && b.groupId) return 1;
    
    // Если оба групповые, сортируем по группе и номеру матча
    if (a.groupId && b.groupId) {
      if (a.group?.name !== b.group?.name) {
        return (a.group?.name || '').localeCompare(b.group?.name || '');
      }
      return (a.matchCode || '').localeCompare(b.matchCode || '');
    }
    
    // Если оба плей-офф, сортируем по этапу (раньше - выше), затем по номеру матча
    if (!a.groupId && !b.groupId) {
      const roundA = getPlayoffRoundMeta(a.matchCode).order;
      const roundB = getPlayoffRoundMeta(b.matchCode).order;
      
      if (roundA !== roundB) {
        return roundA - roundB; // Более ранний этап выше
      }
      
      // Внутри одного этапа сортируем по номеру матча
      const numA = parseInt(a.matchCode?.replace('Playoff-', '') || '0') || 0;
      const numB = parseInt(b.matchCode?.replace('Playoff-', '') || '0') || 0;
      return numA - numB;
    }
    
    return 0;
  });

  // Отладочный вывод для проверки фильтрации
  useEffect(() => {
    if (filteredMatches.length > 0) {
      const playoffFiltered = filteredMatches.filter(m => !m.groupId);
      console.log('Отфильтровано матчей плей-оффа:', playoffFiltered.length);
      console.log('Матчи плей-оффа в filteredMatches:', playoffFiltered.map(m => ({ 
        matchCode: m.matchCode, 
        round: getPlayoffRoundMeta(m.matchCode).order,
        status: m.status 
      })));
    }
  }, [filteredMatches, matches]);

  const completedMatches = matches.filter(m => 
    m.status === 'COMPLETED' || m.status === 'WALKOVER' || m.status === 'RETIRED'
  ).length;
  
  const totalMatches = matches.length;
  const progressPercent = totalMatches > 0 ? (completedMatches / totalMatches) * 100 : 0;
  const isSkeletonSingleElimination =
    levelBracketFormat === 'SINGLE_ELIMINATION' && levelPreScheduleSkeleton;
  const playoffGenerateButtonLabel = isSkeletonSingleElimination
    ? 'Сгенерировать сетку плей-оффа (каркас)'
    : levelBracketFormat === 'SINGLE_ELIMINATION'
      ? 'Сгенерировать первый раунд плей-оффа'
      : 'Сгенерировать матчи плей-оффа';
  const playoffNextRoundButtonLabel = isSkeletonSingleElimination
    ? 'Обновить плей-офф по результатам'
    : 'Создать следующий этап плей-оффа';
  const emptyStateHint = levelBracketFormat === 'SINGLE_ELIMINATION'
    ? isSkeletonSingleElimination
      ? 'Нажмите "Сгенерировать сетку плей-оффа (каркас)" для создания списка матчей'
      : 'Нажмите "Сгенерировать первый раунд плей-оффа" для создания списка матчей'
    : 'Нажмите "Сгенерировать матчи" для создания списка матчей';

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Ввод результатов матчей</h1>

      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Турнир
            </label>
            <select
              value={selectedTournament?.id || ''}
              onChange={(e) => {
                const tournament = tournaments.find(t => t.id === e.target.value);
                setSelectedTournament(tournament || null);
                setSelectedLevel('');
                setMatches([]);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">Выберите турнир</option>
              {tournamentsGrouped.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.items.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Уровень
            </label>
            <select
              value={selectedLevel}
              onChange={(e) => {
                setSelectedLevel(e.target.value);
                setMatches([]);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              disabled={!selectedTournament}
            >
              <option value="">Выберите уровень</option>
              {levels.map(l => (
                <option key={l.level} value={l.level}>
                  {l.level} ({l.playerCount} игроков)
                </option>
              ))}
            </select>
          </div>
        </div>

        {selectedTournament && selectedLevel && (
          <div className="flex items-center gap-4">
            {levelBracketFormat !== 'SINGLE_ELIMINATION' && (
              <button
                onClick={handleGenerateMatches}
                disabled={generating}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                {generating ? 'Проверка...' : 'Сгенерировать матчи'}
              </button>
            )}
            
            <button
              onClick={handleGeneratePlayoffMatches}
              disabled={generating}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:bg-gray-400"
            >
              {generating
                ? 'Генерация...'
                : playoffGenerateButtonLabel}
            </button>
            
            <button
              onClick={handleGenerateNextPlayoffRound}
              disabled={generatingNextRound}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:bg-gray-400"
            >
              {generatingNextRound ? 'Генерация...' : playoffNextRoundButtonLabel}
            </button>

            {levelBracketFormat === 'SINGLE_ELIMINATION' && totalMatches > 0 && (
              <button
                onClick={handleRepairFirstPlayoffRound}
                disabled={repairingFirstRound}
                className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 disabled:bg-gray-400"
              >
                {repairingFirstRound ? 'Восстановление...' : 'Восстановить 1-й раунд'}
              </button>
            )}
            
            {totalMatches > 0 && (
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">
                    Прогресс: {completedMatches} / {totalMatches}
                  </span>
                  <span className="text-sm text-gray-500">
                    {progressPercent.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${progressPercent}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {selectedTournament && selectedLevel && matches.length > 0 && (
        <div className="bg-white p-4 rounded-lg shadow-md mb-6">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Поиск по игрокам
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Введите имя игрока..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-9"
                />
                {searchQuery.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                    aria-label="Сбросить поиск по игроку"
                    title="Сбросить поиск"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Группа
              </label>
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="all">Все группы</option>
                {groups.map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Статус
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="all">Все</option>
                <option value="SCHEDULED">Запланирован</option>
                <option value="IN_PROGRESS">В процессе</option>
                <option value="COMPLETED">Завершён</option>
                <option value="WALKOVER">Walkover</option>
                <option value="RETIRED">Retired</option>
                <option value="CANCELLED">Отменён</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {loading && matches.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <p>Загрузка матчей...</p>
        </div>
      )}

      {!loading && selectedTournament && selectedLevel && matches.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <p>Матчи не найдены</p>
          <p className="text-sm mt-2">
            {emptyStateHint}
          </p>
        </div>
      )}

      {matches.length > 0 && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ЭТАП
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    LEVEL
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    STAGE
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    N
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Игроки
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Статус
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Счёт
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Дата / Время
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Корт
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMatches.map((match) => (
                  <tr key={match.id} className={match.status === 'COMPLETED' ? 'bg-green-50' : ''}>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                      {(match.group?.name
                        ? (match.group.name.trim().toLowerCase().startsWith('группа ')
                          ? match.group.name
                          : `Группа ${match.group.name}`)
                        : null) || (() => {
                        if (match.matchCode?.startsWith('Playoff')) {
                          // Если загружена информация о плей-офф с режимом directFinal
                          if (playoffStage && playoffStage.directFinal && playoffStage.rounds.length > 0) {
                            const matchNum = parseInt(match.matchCode.replace('Playoff-', '')) || 0;
                            
                            // Для режима directFinal определяем раунд по номеру матча
                            if (playoffStage.thirdPlaceMatch) {
                              // Если есть игра за 3-е место, то Playoff-1 = Финал, Playoff-2 = Матч за 3-е место
                              // (матчи генерируются в порядке: сначала финал, потом матч за 3-е место)
                              if (matchNum === 1) {
                                return 'Плей-офф (Финал)';
                              }
                              if (matchNum === 2) {
                                return 'Плей-офф (Матч за 3-е место)';
                              }
                            } else {
                              // Если нет игры за 3-е место, то Playoff-1 = Финал
                              if (matchNum === 1) {
                                return 'Плей-офф (Финал)';
                              }
                            }
                            return 'Плей-офф';
                          }
                          
                          const roundMeta = getPlayoffRoundMeta(match.matchCode);
                          if (roundMeta.label) {
                            return `Плей-офф (${roundMeta.label})`;
                          }
                          return 'Плей-офф';
                        }
                        return match.round?.name || 'Плей-офф';
                      })()}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">
                      {match.level || '-'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">
                      {match.round?.name || '-'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">
                      {match.groupId
                        ? (typeof match.matchNumber === 'number' && match.matchNumber > 0 ? match.matchNumber : '-')
                        : (typeof match.stageMatchNumber === 'number' && match.stageMatchNumber > 0
                            ? match.stageMatchNumber
                            : '-')}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900 whitespace-pre-line leading-snug">
                      {renderPlayersCell(match)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        match.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                        match.status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800' :
                        match.status === 'SCHEDULED' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {match.status === 'COMPLETED' ? 'Завершён' :
                         match.status === 'IN_PROGRESS' ? 'В процессе' :
                         match.status === 'SCHEDULED' ? 'Запланирован' :
                         match.status === 'WALKOVER' ? 'W.O.' :
                         match.status === 'RETIRED' ? 'Retired' :
                         match.status === 'CANCELLED' ? 'Отменён' :
                         match.status}
                      </span>
                    </td>
                    <td 
                      className="px-6 py-4 text-sm font-medium text-gray-900 cursor-pointer hover:text-blue-600"
                      onClick={() => {
                        const canSendNotification = ['COMPLETED', 'IN_PROGRESS', 'WALKOVER', 'RETIRED', 'CANCELLED'].includes(match.status);
                        if (canSendNotification && match.result) {
                          setMatchForSend(match);
                          setShowSendResultModal(true);
                          setSendResultTab('copy');
                        }
                      }}
                      title={['COMPLETED', 'IN_PROGRESS', 'WALKOVER', 'RETIRED', 'CANCELLED'].includes(match.status) && match.result ? 'Кликните для отправки счета' : ''}
                    >
                      <div className="whitespace-pre-line">
                        {formatMatchScore(match)}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-500">
                      <div className="whitespace-nowrap">
                        {(() => {
                          const effectiveDate = match.reservation?.date || match.scheduledDate || null;
                          const effectiveTime = match.reservation?.startTime || match.scheduledTime || '';
                          return effectiveDate
                            ? `${new Date(effectiveDate).toLocaleDateString('ru-RU')} ${effectiveTime}`
                            : '-';
                        })()}
                      </div>
                      <div className="mt-1 text-xs text-gray-400 whitespace-nowrap">
                        {(() => {
                          const effectiveClubName = match.reservation?.court?.club?.name || match.court?.club?.name || '-';
                          const effectiveCourtName = match.reservation?.court?.name || match.court?.name || '-';
                          return `${effectiveClubName} / ${effectiveCourtName}`;
                        })()}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {match.court?.name || '-'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenStageEditor(match)}
                          className="p-2 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded transition-colors"
                          title="Правка LEVEL / STAGE / N"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h7M16 15l2 2 4-4" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleEditMatch(match)}
                          className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded transition-colors"
                          title={match.status === 'COMPLETED' ? 'Изменить результат' : 'Ввести результат'}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteMatch(match)}
                          className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded transition-colors"
                          title="Удалить матч"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editingStageMatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-xl mx-4 my-8">
            <h2 className="text-xl font-bold mb-4">Правка LEVEL / STAGE / N</h2>

            <div className="mb-4 text-sm text-gray-600">
              <div><strong>Матч:</strong> {getPlayerNames(editingStageMatch)}</div>
              <div><strong>Код:</strong> {editingStageMatch.matchCode || '-'}</div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">LEVEL</label>
                <input
                  type="text"
                  value={editingStageLevel}
                  onChange={(e) => setEditingStageLevel(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Например: D-6.5"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">STAGE</label>
                <select
                  value={editingStageRoundId}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    setEditingStageRoundId(selectedId);
                    if (!selectedId) {
                      setEditingStageRoundName('');
                      return;
                    }
                    const selectedOption = editingStageRoundOptions.find((option) => option.id === selectedId);
                    setEditingStageRoundName(selectedOption?.name || '');
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Не выбрано</option>
                  {editingStageRoundOptions.map((option) => (
                    <option key={option.id} value={option.id}>{option.name}</option>
                  ))}
                </select>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button type="button" onClick={() => pickStagePreset('FINAL')} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50">Финал</button>
                  <button type="button" onClick={() => pickStagePreset('SEMI')} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50">1/2</button>
                  <button type="button" onClick={() => pickStagePreset('QUARTER')} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50">1/4</button>
                  <button type="button" onClick={() => pickStagePreset('EIGHTH')} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50">1/8</button>
                  <button type="button" onClick={() => pickStagePreset('SIXTEENTH')} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50">1/16</button>
                  <button type="button" onClick={() => pickStagePreset('THIRTY_SECOND')} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50">1/32</button>
                  <button type="button" onClick={() => pickStagePreset('SIXTY_FOURTH')} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50">1/64</button>
                  <button type="button" onClick={() => pickStagePreset('THIRD_PLACE')} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50">3-е место</button>
                </div>
                {!editingStageRoundId && editingStageRoundName && (
                  <div className="mt-2 text-xs text-indigo-700">
                    Пресет: <strong>{editingStageRoundName}</strong> (будет создан/привязан при сохранении)
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">N</label>
                <input
                  type="number"
                  min={1}
                  value={editingStageNumber}
                  onChange={(e) => setEditingStageNumber(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Порядковый номер матча"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setEditingStageMatch(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={savingStageMeta}
              >
                Отмена
              </button>
              <button
                onClick={handleSaveStageMeta}
                disabled={savingStageMeta}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {savingStageMeta ? 'Сохраняем...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для ввода результата */}
      {editingMatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 my-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Ввод результата матча</h2>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Группа:</strong> {editingMatch.group?.name}
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Игроки:</strong> {getPlayerNames(editingMatch)}
              </p>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Дата и время матча
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <input
                    type="date"
                    value={resultDate}
                    onChange={(e) => setResultDate(e.target.value)}
                    disabled={resultDateTimeLocked}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
                  />
                  <input
                    type="time"
                    value={resultTime}
                    onChange={(e) => setResultTime(e.target.value)}
                    disabled={resultDateTimeLocked}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
                  />
                  <select
                    value={resultClubId}
                    onChange={(e) => {
                      const nextClubId = e.target.value;
                      setResultClubId(nextClubId);
                      if (!nextClubId) {
                        setResultCourtId('');
                        return;
                      }
                      if (!courts.some((c) => c.id === resultCourtId && c.clubId === nextClubId)) {
                        setResultCourtId('');
                      }
                    }}
                    disabled={resultDateTimeLocked}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
                  >
                    <option value="">Клуб</option>
                    {clubs.map((club) => (
                      <option key={club.id} value={club.id}>
                        {club.name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={resultCourtId}
                    onChange={(e) => setResultCourtId(e.target.value)}
                    disabled={resultDateTimeLocked || !resultClubId}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
                  >
                    <option value="">Корт</option>
                    {courts
                      .filter((court) => court.clubId === resultClubId)
                      .map((court) => (
                        <option key={court.id} value={court.id}>
                          {court.name}
                        </option>
                      ))}
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {resultDateTimeLocked
                    ? 'Дата, время, клуб и корт взяты из резервирования.'
                    : 'Для матчей без резерва укажите дату/время, клуб и корт вручную.'}
                </p>
              </div>
              
              {/* Выбор формата матча */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Формат матча
                </label>
                <select
                  value={matchFormat?.id || ''}
                  onChange={async (e) => {
                    const formatId = e.target.value;
                    if (formatId) {
                      const selectedFormat = availableFormats.find(f => f.id === formatId);
                      if (selectedFormat) {
                        setMatchFormat(selectedFormat);
                        
                        // Обновляем сеты на основе нового формата
                        // Важно: используем именно maxSets из формата, без fallback на 3
                        const maxSets = selectedFormat.maxSets !== undefined ? selectedFormat.maxSets : 3;
                        const newSets: SetScore[] = [];
                        const currentSetsMap = new Map(sets.map(s => [s.set, s]));
                        
                        // Создаем новый массив сетов точно по количеству maxSets
                        for (let i = 1; i <= maxSets; i++) {
                          const existingSet = currentSetsMap.get(i);
                          if (existingSet) {
                            newSets.push(existingSet);
                          } else {
                            newSets.push({ set: i, player1: 0, player2: 0, tiebreak: null });
                          }
                        }
                        
                        // Убеждаемся, что массив имеет ровно maxSets элементов
                        while (newSets.length > maxSets) {
                          newSets.pop();
                        }
                        while (newSets.length < maxSets) {
                          newSets.push({ set: newSets.length + 1, player1: 0, player2: 0, tiebreak: null });
                        }
                        
                        setSets(newSets);
                      }
                    }
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  {availableFormats.map(format => (
                    <option key={format.id} value={format.id}>
                      {format.name}
                    </option>
                  ))}
                </select>
                {matchFormat && (
                  <p className="text-xs text-gray-500 mt-1">
                    Тайбрейк: {matchFormat.hasTiebreak ? `до ${matchFormat.tiebreakTo || 7}` : 'нет'}
                    {matchFormat.superTiebreak && ` • Супер-тайбрейк: до ${matchFormat.superTiebreakTo || 10}`}
                  </p>
                )}
              </div>
            </div>

            {/* Специальные статусы */}
            <div className="mb-6 border-b pb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Другие статусы
              </label>
              <select
                value={specialStatusSelectValue}
                onChange={(e) => {
                  const type = e.target.value as '' | 'REFUSAL' | 'DISQUALIFICATION' | 'INJURY' | 'NO_SHOW' | SuspensionStatusOption;
                  if (!type) {
                    setSpecialStatus({ type: null, player: null, reason: '' });
                    setSuspensionStatusOption('SUSPENSION_OTHER');
                  } else if (type === 'SUSPENSION_TIME' || type === 'SUSPENSION_WEATHER' || type === 'SUSPENSION_OTHER') {
                    const suspensionType = type as SuspensionStatusOption;
                    setSuspensionStatusOption(suspensionType);
                    setSpecialStatus({
                      type: 'SUSPENSION',
                      player: null,
                      reason: '',
                      reasonCode:
                        suspensionType === 'SUSPENSION_WEATHER'
                          ? 'WEATHER'
                          : suspensionType === 'SUSPENSION_TIME'
                            ? 'TIME'
                            : 'OTHER'
                    });
                  } else {
                    setSuspensionStatusOption('SUSPENSION_OTHER');
                    setSpecialStatus({ ...specialStatus, type: type as any, player: null, reasonCode: undefined });
                  }
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                    <option value="">Нет</option>
                    <option value="REFUSAL">Отказ</option>
                    <option value="DISQUALIFICATION">Дисквалификация</option>
                    <option value="INJURY">Травма</option>
                    <option value="NO_SHOW">Неявка</option>
                    <option value="SUSPENSION_WEATHER">Погода</option>
                    <option value="SUSPENSION_TIME">Время</option>
                    <option value="SUSPENSION_OTHER">Прочее</option>
                  </select>
                  
                  {specialStatus.type && (
                    <div className="mt-2 space-y-2">
                      {(specialStatus.type === 'REFUSAL' || specialStatus.type === 'DISQUALIFICATION' || 
                        specialStatus.type === 'INJURY' || specialStatus.type === 'NO_SHOW') && (
                        <select
                          value={specialStatus.player || ''}
                          onChange={(e) => setSpecialStatus({ ...specialStatus, player: e.target.value as 'player1' | 'player2' || null })}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        >
                          <option value="">Выберите игрока</option>
                          <option value="player1">{editingMatch.player1?.name || 'Игрок 1'}</option>
                          <option value="player2">{editingMatch.player2?.name || 'Игрок 2'}</option>
                        </select>
                      )}
              <input
                type="text"
                        value={specialStatus.reason}
                        onChange={(e) => setSpecialStatus({ ...specialStatus, reason: e.target.value })}
                        placeholder={
                          specialStatus.type === 'SUSPENSION' 
                            ? suspensionStatusOption === 'SUSPENSION_WEATHER'
                              ? 'Причина приостановки (погода)'
                              : suspensionStatusOption === 'SUSPENSION_TIME'
                                ? 'Причина приостановки (время)'
                                : 'Причина приостановки (прочее)'
                            : 'Причина (необязательно)'
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                  )}
            </div>

            <div className="mb-6 border-b pb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Административные флаги
              </label>
              {(() => {
                const activeSlots = ADMIN_SLOTS.filter((slot) => Boolean(editingMatch?.[`${slot}Id` as keyof Match]));
                const allStatsExcluded =
                  activeSlots.length > 0 &&
                  activeSlots.every((slot) => (administrativePlayers[slot] || createDefaultAdministrativePlayerFlags()).excludeFromStats);
                return (
                  <div className="mb-3 space-y-2">
                    <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        checked={allStatsExcluded}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setAdministrativePlayers((prev) => {
                            const next = { ...prev };
                            activeSlots.forEach((slot) => {
                              next[slot] = {
                                ...(next[slot] || createDefaultAdministrativePlayerFlags()),
                                excludeFromStats: checked
                              };
                            });
                            return next;
                          });
                        }}
                      />
                      Исключить из статистики для всех участников
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={administrativeExcludeFromElo}
                        onChange={(e) => setAdministrativeExcludeFromElo(e.target.checked)}
                      />
                      Исключить матч из Elo для всех участников
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={administrativeSuppressTelegramResults}
                        onChange={(e) => setAdministrativeSuppressTelegramResults(e.target.checked)}
                      />
                      Не публиковать результат в группы
                    </label>
                  </div>
                );
              })()}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {ADMIN_SLOTS
                  .filter((slot) => Boolean(editingMatch?.[`${slot}Id` as keyof Match]))
                  .map((slot) => {
                    const playerLabel = (() => {
                      if (slot === 'player1') return editingMatch?.player1?.name || 'Игрок 1';
                      if (slot === 'player2') return editingMatch?.player2?.name || 'Игрок 2';
                      if (slot === 'player3') return editingMatch?.player3?.name || 'Игрок 3';
                      return editingMatch?.player4?.name || 'Игрок 4';
                    })();
                    const flags = administrativePlayers[slot] || createDefaultAdministrativePlayerFlags();
                    return (
                      <div key={slot} className="border border-gray-200 rounded-lg p-3">
                        <div className="text-sm font-medium text-gray-700 mb-2">{playerLabel}</div>
                        <div className="grid grid-cols-1 gap-2">
                          <label className="inline-flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={flags.noShow}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setAdministrativePlayers((prev) => ({
                                  ...prev,
                                  [slot]: { ...(prev[slot] || createDefaultAdministrativePlayerFlags()), noShow: checked }
                                }));
                                if (checked) setAdministrativeExcludeFromElo(true);
                              }}
                            />
                            Неявка
                          </label>
                          <label className="inline-flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={flags.excludeFromRatingPoints}
                              onChange={(e) =>
                                setAdministrativePlayers((prev) => ({
                                  ...prev,
                                  [slot]: { ...(prev[slot] || createDefaultAdministrativePlayerFlags()), excludeFromRatingPoints: e.target.checked }
                                }))
                              }
                            />
                            Исключить из рейтинг-очков
                          </label>
                          <label className="inline-flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={flags.cancelEntryFeeAccrual}
                              onChange={(e) =>
                                setAdministrativePlayers((prev) => ({
                                  ...prev,
                                  [slot]: { ...(prev[slot] || createDefaultAdministrativePlayerFlags()), cancelEntryFeeAccrual: e.target.checked }
                                }))
                              }
                            />
                            Аннулировать начисление взноса
                          </label>
                        </div>
                      </div>
                    );
                  })}
              </div>
              <div className="mt-3 space-y-2">
                <input
                  type="text"
                  value={administrativeReason}
                  onChange={(e) => setAdministrativeReason(e.target.value)}
                  placeholder="Комментарий/причина (необязательно)"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>

            {/* Ввод сета */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Счёт по сетам
              </label>
                <div className="space-y-4">
                  {sets.map((set, index) => {
                    // Определяем, является ли этот сет решающим тайбрейком
                    const isLastSet = index === sets.length - 1;
                    let isSuperTiebreak = false;
                    let isPotentialSuperTiebreak = false;
                    
                    // Решающий тайбрейк (super-tiebreak) используется только в форматах "До 3 сетов"
                    // когда счет по сетам 1:1, и вместо третьего сета играется решающий тайбрейк до 10
                    // Для формата "До 1 сета" при счете 6:6 играется обычный тайбрейк до 7, а не решающий
                    const hasSuperTiebreak = matchFormat?.superTiebreak === true || 
                                           matchFormat?.superTiebreak === 1 ||
                                           (matchFormat?.name && matchFormat.name.toLowerCase().includes('супер-тайбрейк'));
                    
                    // Решающий тайбрейк возможен только если формат имеет maxSets >= 2 (минимум 2 сета)
                    if (isLastSet && hasSuperTiebreak && matchFormat && matchFormat.maxSets >= 2) {
                      // Подсчитываем счет по сетам перед последним сетом
                      let setsBeforeLastPlayer1 = 0;
                      let setsBeforeLastPlayer2 = 0;
                      let completedSetsCount = 0;
                      
                      for (let i = 0; i < sets.length - 1; i++) {
                        const prevSet = sets[i];
                        // Проверяем, что сет завершен (не нулевой счет)
                        if (prevSet.player1 > 0 || prevSet.player2 > 0) {
                          completedSetsCount++;
                          if (prevSet.player1 > prevSet.player2) {
                            setsBeforeLastPlayer1++;
                          } else if (prevSet.player2 > prevSet.player1) {
                            setsBeforeLastPlayer2++;
                          }
                        }
                      }
                      
                      // Если счет по сетам равный (1:1) и есть хотя бы 2 завершенных сета, это решающий тайбрейк
                      if (completedSetsCount >= 2 && setsBeforeLastPlayer1 === setsBeforeLastPlayer2 && setsBeforeLastPlayer1 > 0) {
                        isSuperTiebreak = true;
                      } else {
                        // В остальных случаях показываем как потенциальный решающий тайбрейк
                        isPotentialSuperTiebreak = true;
                      }
                    }
                    // Для формата "До 1 сета" не устанавливаем isSuperTiebreak или isPotentialSuperTiebreak
                    // так как при счете 6:6 играется обычный тайбрейк до 7
                    
                    return (
                    <div key={set.set} className={`border rounded-lg p-4 ${
                      isSuperTiebreak ? 'border-blue-400 bg-blue-50' : 
                      isPotentialSuperTiebreak ? 'border-blue-300 bg-blue-50/50' : 
                      'border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-sm">Сет {set.set}</h3>
                          {(isSuperTiebreak || isPotentialSuperTiebreak) && (
                            <span className={`px-2 py-0.5 text-white text-xs font-semibold rounded ${
                              isSuperTiebreak ? 'bg-blue-600' : 'bg-blue-500'
                            }`}>
                              {isSuperTiebreak 
                                ? 'Решающий тайбрейк' 
                                : 'Решающий тайбрейк (если счет 1:1)'}
                            </span>
                          )}
                        </div>
                        {(set.player1 > 0 || set.player2 > 0) && (
                          <button
                            onClick={() => {
                              const newSets = [...sets];
                              newSets[index] = { set: set.set, player1: 0, player2: 0, tiebreak: null, superTiebreak: false };
                              setSets(newSets);
                            }}
                            className="text-xs text-red-600 hover:text-red-800"
                          >
                            Очистить
                          </button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-5">
                          <label className="block text-xs text-gray-600 mb-1">
                            {(() => {
                              const isDoubles = selectedTournament?.format === 'DOUBLES';
                              if (isDoubles && editingMatch.player3) {
                                return `${editingMatch.player1?.name || 'Игрок 1'} / ${editingMatch.player3.name}`;
                              }
                              return editingMatch.player1?.name || 'Игрок 1';
                            })()}
                          </label>
                          <input
                            type="number"
                            min="0"
                            max={isSuperTiebreak ? (matchFormat?.superTiebreakTo || 10) : 7}
                            value={set.player1 || ''}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 0;
                              const newSets = [...sets];
                              const currentSet = newSets[index];
                              
                              newSets[index] = { ...currentSet, player1: value, superTiebreak: isSuperTiebreak };
                              
                              // Активация тайбрейка при счете 7:6 или 6:7
                              if (matchFormat?.hasTiebreak && !isSuperTiebreak) {
                                const newPlayer2 = newSets[index].player2;
                                // Если счет 7:6 или 6:7, активируем поле тайбрейка
                                if ((value === 7 && newPlayer2 === 6) || (value === 6 && newPlayer2 === 7)) {
                                  // Если тайбрейк еще не заполнен, оставляем null (пользователь введет)
                                  // Поле тайбрейка будет показано для ввода
                                  if (!newSets[index].tiebreak) {
                                    // Поле будет показано, но не заполнено автоматически
                                  }
                                } else if (value === 6 && newPlayer2 === 6) {
                                  // При счете 6:6 поле тайбрейка тоже показывается
                                  // Тайбрейк будет заполнен отдельно
                                } else {
                                  // Если счет не 6:6, 7:6 или 6:7, сбрасываем тайбрейк
                                  newSets[index].tiebreak = null;
                                }
                              } else if (value !== 6 || newSets[index].player2 !== 6 || isSuperTiebreak) {
                                newSets[index].tiebreak = null;
                              }
                              
                              setSets(newSets);
                            }}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            placeholder="0"
                          />
                        </div>
                        
                        <div className="col-span-2 text-center text-gray-500 font-bold">-</div>
                        
                        <div className="col-span-5">
                          <label className="block text-xs text-gray-600 mb-1">
                            {(() => {
                              const isDoubles = selectedTournament?.format === 'DOUBLES';
                              if (isDoubles && editingMatch.player4) {
                                return `${editingMatch.player2?.name || 'Игрок 2'} / ${editingMatch.player4.name}`;
                              }
                              return editingMatch.player2?.name || 'Игрок 2';
                            })()}
                          </label>
                          <input
                            type="number"
                            min="0"
                            max={isSuperTiebreak ? (matchFormat?.superTiebreakTo || 10) : 7}
                            value={set.player2 || ''}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 0;
                              const newSets = [...sets];
                              const currentSet = newSets[index];
                              
                              newSets[index] = { ...currentSet, player2: value, superTiebreak: isSuperTiebreak };
                              
                              // Активация тайбрейка при счете 7:6 или 6:7
                              if (matchFormat?.hasTiebreak && !isSuperTiebreak) {
                                const newPlayer1 = newSets[index].player1;
                                // Если счет 7:6 или 6:7, активируем поле тайбрейка
                                if ((newPlayer1 === 7 && value === 6) || (newPlayer1 === 6 && value === 7)) {
                                  // Если тайбрейк еще не заполнен, оставляем null (пользователь введет)
                                  // Поле тайбрейка будет показано для ввода
                                  if (!newSets[index].tiebreak) {
                                    // Поле будет показано, но не заполнено автоматически
                                  }
                                } else if (newPlayer1 === 6 && value === 6) {
                                  // При счете 6:6 поле тайбрейка тоже показывается
                                  // Тайбрейк будет заполнен отдельно
                                } else {
                                  // Если счет не 6:6, 7:6 или 6:7, сбрасываем тайбрейк
                                  newSets[index].tiebreak = null;
                                }
                              } else if (value !== 6 || newSets[index].player1 !== 6 || isSuperTiebreak) {
                                newSets[index].tiebreak = null;
                              }
                              
                              setSets(newSets);
                            }}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            placeholder="0"
                          />
                        </div>
                      </div>
                      
                      {/* Поле для тайбрейка (только для обычных сетов, не для решающего тайбрейка) */}
                      {/* Показывается при счете 6:6, 7:6 или 6:7 */}
                      {!isSuperTiebreak && matchFormat?.hasTiebreak && (
                        ((set.player1 === 6 && set.player2 === 6) || 
                         (set.player1 === 7 && set.player2 === 6) || 
                         (set.player1 === 6 && set.player2 === 7)) && (
                        <div className={`mt-3 p-3 rounded-lg ${
                          (set.player1 === 7 && set.player2 === 6) || (set.player1 === 6 && set.player2 === 7)
                            ? 'bg-blue-50 border-2 border-blue-300'
                            : 'border border-gray-200'
                        }`}>
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            Тайбрейк (до {matchFormat.tiebreakTo || 7})
                            {((set.player1 === 7 && set.player2 === 6) || (set.player1 === 6 && set.player2 === 7)) && (
                              <span className="ml-2 text-blue-600 font-medium">✓ Активирован</span>
                            )}
                          </label>
                          <div className="grid grid-cols-12 gap-2 items-center">
                            <div className="col-span-5">
                              <label className="block text-xs text-gray-600 mb-1">
                                {(() => {
                                  const isDoubles = selectedTournament?.format === 'DOUBLES';
                                  if (isDoubles && editingMatch.player3) {
                                    return `${editingMatch.player1?.name || 'Игрок 1'} / ${editingMatch.player3.name}`;
                                  }
                                  return editingMatch.player1?.name || 'Игрок 1';
                                })()}
                              </label>
                              <input
                                type="number"
                                min="0"
                                max="20"
                                value={set.tiebreak && typeof set.tiebreak === 'object' ? (set.tiebreak.player1 || '') : ''}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value) || 0;
                                  const newSets = [...sets];
                                  const currentTiebreak = set.tiebreak;
                                  if (currentTiebreak && typeof currentTiebreak === 'object') {
                                    newSets[index] = { ...set, tiebreak: { ...currentTiebreak, player1: value } };
                                  } else {
                                    newSets[index] = { ...set, tiebreak: { player1: value, player2: 0 } };
                                  }
                                  setSets(newSets);
                                }}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                placeholder="0"
                              />
                            </div>
                            <div className="col-span-2 text-center text-gray-500 font-bold">:</div>
                            <div className="col-span-5">
                              <label className="block text-xs text-gray-600 mb-1">
                                {(() => {
                                  const isDoubles = selectedTournament?.format === 'DOUBLES';
                                  if (isDoubles && editingMatch.player4) {
                                    return `${editingMatch.player2?.name || 'Игрок 2'} / ${editingMatch.player4.name}`;
                                  }
                                  return editingMatch.player2?.name || 'Игрок 2';
                                })()}
                              </label>
                              <input
                                type="number"
                                min="0"
                                max="20"
                                value={set.tiebreak && typeof set.tiebreak === 'object' ? (set.tiebreak.player2 || '') : ''}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value) || 0;
                                  const newSets = [...sets];
                                  const currentTiebreak = set.tiebreak;
                                  if (currentTiebreak && typeof currentTiebreak === 'object') {
                                    newSets[index] = { ...set, tiebreak: { ...currentTiebreak, player2: value } };
                                  } else {
                                    newSets[index] = { ...set, tiebreak: { player1: 0, player2: value } };
                                  }
                                  setSets(newSets);
                                }}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                placeholder="0"
                              />
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Укажите полный счет тайбрейка (минимум {matchFormat.tiebreakTo || 7} очков для победителя, разница минимум 2)
              </p>
            </div>
                        )
                      )}
                      
                      {/* Информация для решающего тайбрейка */}
                      {isSuperTiebreak && (
                        <div className="mt-3 p-2 bg-blue-100 rounded border border-blue-300">
                          <p className="text-xs text-blue-800 font-medium">
                            Решающий тайбрейк (до {matchFormat?.superTiebreakTo || 10}): счет по очкам в тайбрейке. 
                            Разница минимум 2 очка. Победитель тайбрейка выигрывает сет и матч.
                          </p>
                        </div>
                      )}
                      
                      {/* Информация для потенциального решающего тайбрейка */}
                      {isPotentialSuperTiebreak && !isSuperTiebreak && (
                        <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
                          <p className="text-xs text-blue-700">
                            Если счет по первым сетам будет равным (1:1), этот сет будет решающим тайбрейком (до {matchFormat?.superTiebreakTo || 10} очков). Разница минимум 2 очка. Победитель тайбрейка выигрывает сет и матч.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                  })}
                </div>
            </div>

            {/* Ошибки валидации */}
            {validationErrors.length > 0 && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm font-medium text-red-800 mb-1">Ошибки валидации:</p>
                <ul className="list-disc list-inside text-xs text-red-700 space-y-1">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={() => handleSaveResult()}
                disabled={savingResult}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingResult ? 'Сохранение...' : 'Сохранить'}
              </button>
              <button
                onClick={() => handleSaveResult({ updateTelegramMessage: true })}
                disabled={savingResult}
                className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingResult ? 'Сохранение...' : 'Сохранить и обновить'}
              </button>
              {editingMatch && (editingMatch.result || editingMatch.score) && (
                <button
                  onClick={handleDeleteResult}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700"
                >
                  Удалить результат
                </button>
              )}
              <button
                onClick={() => {
                  setEditingMatch(null);
                  setResultInput('');
                  setSets([]);
                  setSpecialStatus({ type: null, player: null, reason: '' });
      setSuspensionStatusOption('SUSPENSION_OTHER');
                  setWalkover(false);
                  setRetired({ player: null, reason: '' });
                  setValidationErrors([]);
                  setMatchFormat(null);
                  setResultDate('');
                  setResultTime('');
                  setResultDateTimeLocked(false);
                  setAdministrativePlayers({});
                  setAdministrativeExcludeFromElo(false);
                  setAdministrativeSuppressTelegramResults(false);
                  setAdministrativeReason('');
      setResultClubId('');
      setResultCourtId('');
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {showGeneratePreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-3">
              Предпросмотр генерации матчей
            </h2>

            <div className="text-sm text-gray-700 mb-4 space-y-1">
              <div>Турнир: <span className="font-medium">{selectedTournament?.name}</span></div>
              <div>Уровень: <span className="font-medium">{selectedLevel}</span></div>
              <div>
                Будет создано: <span className="font-medium">{generatePreviewData?.toCreate ?? 0}</span> из{' '}
                <span className="font-medium">{generatePreviewData?.total ?? 0}</span> возможных.
                Уже существует: <span className="font-medium">{generatePreviewData?.existing ?? 0}</span>.
              </div>
            </div>

            {generatePreviewData?.message && (
              <div className="mb-4 p-3 rounded bg-yellow-50 text-yellow-800 text-sm">
                {generatePreviewData.message}
              </div>
            )}

            {(generatePreviewData?.matches?.length || 0) === 0 ? (
              <div className="mb-4 p-4 rounded bg-green-50 text-green-800 text-sm">
                Пропущенных матчей не найдено. Создавать ничего не нужно.
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
                <div className="max-h-[50vh] overflow-y-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Группа</th>
                        <th className="px-3 py-2 text-left">Матч</th>
                        <th className="px-3 py-2 text-left">Почему будет создан</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {(generatePreviewData?.matches || []).map((item, idx) => (
                        <tr key={`${item.groupId}_${item.player1Id || ''}_${item.player2Id || ''}_${idx}`}>
                          <td className="px-3 py-2 whitespace-nowrap">{item.groupName} ({item.level})</td>
                          <td className="px-3 py-2">{item.participantsLabel}</td>
                          <td className="px-3 py-2 text-gray-600">{item.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  if (generating) return;
                  setShowGeneratePreviewModal(false);
                  setGeneratePreviewData(null);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                disabled={generating}
              >
                Отмена
              </button>
              <button
                onClick={handleConfirmGenerateMatches}
                disabled={generating || (generatePreviewData?.matches?.length || 0) === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {generating ? 'Создание...' : `Подтвердить создание (${generatePreviewData?.toCreate ?? 0})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для отправки результата матча */}
      {showSendResultModal && matchForSend && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              Отправить результат матча
            </h2>
            
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-700 whitespace-pre-line">
                {formatMatchResultText(matchForSend)}
              </div>
            </div>

            {/* Табы для выбора способа отправки */}
            <div className="mb-4 border-b border-gray-200">
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setSendResultTab('copy');
                  }}
                  className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                    sendResultTab === 'copy'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Копировать
                </button>
                <button
                  onClick={() => {
                    setSendResultTab('groups');
                    if (allGroups.length === 0) {
                      fetchGroupsForResult();
                    }
                  }}
                  className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                    sendResultTab === 'groups'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Группы
                </button>
              </div>
            </div>

            {sendResultTab === 'copy' ? (
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Нажмите кнопку "Копировать" для копирования результата в буфер обмена.
                </p>
              </div>
            ) : (
              loadingGroups ? (
                <div className="text-center py-8">Загрузка списка групп...</div>
              ) : (
                <>
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-sm font-medium text-gray-700">
                        Выберите группы и топики для отправки результата:
                      </label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            const allGroupsAndTopics = new Set<string>();
                            allGroups.forEach(group => {
                              // Добавляем основную группу (без топика)
                              allGroupsAndTopics.add(group.chatId);
                              // Добавляем все топики группы
                              group.topics.forEach(topic => {
                                allGroupsAndTopics.add(`${group.chatId}:${topic.threadId}`);
                              });
                            });
                            setSelectedGroupsAndTopicsForResult(allGroupsAndTopics);
                          }}
                          className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                        >
                          Выбрать все
                        </button>
                        <button
                          onClick={() => setSelectedGroupsAndTopicsForResult(new Set())}
                          className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                        >
                          Снять все
                        </button>
                      </div>
                    </div>
                    
                    <div className="border border-gray-300 rounded-lg max-h-96 overflow-y-auto">
                      {allGroups.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">Нет групп</div>
                      ) : (
                        <div className="divide-y divide-gray-200">
                          {[...allGroups]
                            .sort((a, b) => {
                              const weight = (group: any): number => {
                                const groupKey = group.chatId;
                                if (selectedGroupsAndTopicsForResult.has(groupKey)) return 2;
                                const hasSelectedTopic = Array.isArray(group.topics) && group.topics.some((topic: any) =>
                                  selectedGroupsAndTopicsForResult.has(`${group.chatId}:${topic.threadId}`)
                                );
                                return hasSelectedTopic ? 1 : 0;
                              };
                              return weight(b) - weight(a);
                            })
                            .map((group) => {
                            const groupKey = group.chatId;
                            const isGroupSelected = selectedGroupsAndTopicsForResult.has(groupKey);
                            const groupDisplayName = group.title || group.username || group.chatId;
                            
                            return (
                              <div key={group.chatId} className="p-3">
                                {/* Группа (основной поток) */}
                                <label className="flex items-center p-2 hover:bg-gray-50 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={isGroupSelected}
                                    onChange={(e) => {
                                      const newSet = new Set(selectedGroupsAndTopicsForResult);
                                      if (e.target.checked) {
                                        newSet.add(groupKey);
                                      } else {
                                        newSet.delete(groupKey);
                                        // При снятии группы также снимаем все топики
                                        group.topics.forEach(topic => {
                                          newSet.delete(`${group.chatId}:${topic.threadId}`);
                                        });
                                      }
                                      setSelectedGroupsAndTopicsForResult(newSet);
                                    }}
                                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">
                                      {groupDisplayName}
                                      {!group.title && !group.username && (
                                        <span className="text-xs text-gray-500 ml-2">(приватная группа)</span>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-500">Основной поток</div>
                                  </div>
                                </label>
                                
                                {/* Топики группы */}
                                {group.topics && group.topics.length > 0 && (
                                  <div className="ml-8 mt-2 space-y-1">
                                    {group.topics.map((topic: any) => {
                                      const topicKey = `${group.chatId}:${topic.threadId}`;
                                      const isTopicSelected = selectedGroupsAndTopicsForResult.has(topicKey);
                                      
                                      return (
                                        <label
                                          key={topic.id}
                                          className="flex items-center p-2 hover:bg-gray-50 cursor-pointer"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isTopicSelected}
                                            onChange={(e) => {
                                              const newSet = new Set(selectedGroupsAndTopicsForResult);
                                              if (e.target.checked) {
                                                newSet.add(topicKey);
                                              } else {
                                                newSet.delete(topicKey);
                                              }
                                              setSelectedGroupsAndTopicsForResult(newSet);
                                            }}
                                            className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                          />
                                          <div className="flex-1">
                                            <div className="text-sm text-gray-700">
                                              {topic.name || `Топик ${topic.threadId}`}
                                            </div>
                                          </div>
                                        </label>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-2 text-sm text-gray-600">
                      Выбрано: {selectedGroupsAndTopicsForResult.size} групп/топиков
                    </div>
                  </div>
                </>
              )
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => {
                  setShowSendResultModal(false);
                  setMatchForSend(null);
                  setSendResultTab('copy');
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                disabled={sendingResult}
              >
                Отмена
              </button>
              <button
                onClick={handleSendResult}
                disabled={sendingResult || 
                  (sendResultTab === 'groups' && selectedGroupsAndTopicsForResult.size === 0)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {sendingResult ? 'Отправка...' : 
                  sendResultTab === 'copy' 
                    ? 'Копировать'
                    : `Отправить (${selectedGroupsAndTopicsForResult.size})`}
              </button>
              {sendResultTab === 'copy' && (
                <button
                  onClick={async () => {
                    if (!matchForSend) return;
                    try {
                      setSendingResult(true);
                      await copyYoutubeScoreToClipboard(matchForSend);
                      setShowSendResultModal(false);
                      setMatchForSend(null);
                      setSendResultTab('copy');
                    } finally {
                      setSendingResult(false);
                    }
                  }}
                  disabled={sendingResult}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Youtube
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchResults;


